import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fetchAdmins, addAdmin, removeAdmin, addTrustedDomain, removeTrustedDomain, listStaffDomains, addStaffDomain, removeStaffDomain, fetchDbConfig, testDbConfig, saveDbConfig, resetDbConfig, fetchDbOpsConfig, testDbOpsConfig, diagnoseDbOpsConfig, manualSelfAllowDbOps, saveDbOpsConfig, resetDbOpsConfig, fetchDataAccessStatus, setDataAccessMode, previewDataAccessComparison, getFilesBaseUrl, setFilesBaseUrl, getDriveConfig, testDriveConfig, saveDriveConfig, rescanDriveSubfolders, checkDbStatus, fetchEgressIp, fetchAccessMe, fetchAccessPermissions, fetchAccessRoles, fetchAccessUsers, upsertAccessUser, deleteAccessUser, searchAccessPeople, upsertAccessRole, deleteAccessRole, advisorCheckPermission, advisorCheckRole, advisorAnalyseAssignment, advisorChat, advisorApply, advisorUndo, advisorHistory, fetchRecentQuoteChanges, fetchEmailRouting, saveEmailRouting, sendTestEmail, fetchPwaBranding, updatePwaBranding, resetPwaBranding, uploadPwaIcon, fetchShippingCredentials, saveShippingCredentials, deleteShippingCredentials, deleteShippingCredentialField, testShippingCredentials, fetchShippingHealth } from "@/lib/api";
import { refreshBranding, useBranding } from "@/lib/branding";
import { TOURS } from "@/lib/tours";
import AllToursModal from "@/components/AllToursModal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X, Plus, ShieldCheck, Trash, Warning, Database, ArrowCounterClockwise, CheckCircle, Key, MagnifyingGlass, UserCircle, Robot, PaperPlaneRight, Sparkle, ArrowUUpLeft, Clock, Receipt, EnvelopeSimple, DeviceMobile, Upload, ArrowsClockwise, BookOpen, ArrowUp, ArrowDown, Globe, Copy, CaretDown, CaretRight, LockKey, Truck, Eye, EyeSlash, ArrowsLeftRight } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import DriveFolderPicker from "@/components/DriveFolderPicker";
import DriveAuditModal from "@/components/DriveAuditModal";
import DriveSmartLocator from "@/components/DriveSmartLocator";

// Hard-coded defaults used to pre-fill the DB form the first time an admin opens it.
// Matches the creds currently in backend/.env so new deploys can recover from a bad env push.
const DB_DEFAULTS = {
  host: "93.127.200.65",
  database: "ipqhznmy_UsAir",
  user: "ipqhznmy_dbroot",
  password: "12_dP%0bV-;S",
  port: 3306,
};

export default function AdminPanel({ user, onClose, mode = "modal" }) {
  const [tab, setTab] = useState("admins");
  const [admins, setAdmins] = useState([]);
  const [protectedEmail, setProtectedEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dbStatus, setDbStatus] = useState(null); // { connected, primary, fallback, fallback_used }
  const [toursOpen, setToursOpen] = useState(false);
  // iter150AZ.87 — Super-Admins can add admins with ANY email domain.
  // We pull this from /access/me on mount so the UI can soften the
  // "Only emails ending with …" copy + remove the implicit domain hint
  // from the placeholder.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // iter150AZ.88 — Runtime-managed trusted-domain whitelist
  const [trustedDomains, setTrustedDomains] = useState([]);        // full effective list
  const [trustedDomainsDefault, setTrustedDomainsDefault] = useState([]); // env-baked subset
  const [newDomain, setNewDomain] = useState("");
  const [domainBusy, setDomainBusy] = useState(false);
  // iter200 — Runtime-managed main-company staff domains
  const [staffDomains, setStaffDomains] = useState({ built_in: [], extras: [], effective: [] });
  const [newStaffDomain, setNewStaffDomain] = useState("");
  const [staffDomainBusy, setStaffDomainBusy] = useState(false);

  const load = () => {
    fetchAdmins().then((d) => {
      setAdmins(d.admins);
      setProtectedEmail(d.protected);
      setTrustedDomains(d.trusted_domains || []);
      setTrustedDomainsDefault(d.trusted_domains_default || []);
    });
    // Fetch the probe result so we can show an in-app banner when the admin override failed.
    checkDbStatus().then(setDbStatus).catch(() => {});
    fetchAccessMe().then((m) => setIsSuperAdmin(!!m?.is_super_admin)).catch(() => {});
    listStaffDomains().then(setStaffDomains).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setError("");
    setLoading(true);
    try {
      const res = await addAdmin(newEmail, user.email);
      setAdmins(res.admins);
      setNewEmail("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add admin");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (email) => {
    setError("");
    try {
      const res = await removeAdmin(email, user.email);
      setAdmins(res.admins);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove admin");
    }
  };

  // iter150AZ.88 — Trusted-domain management (super-admin only)
  const handleAddDomain = async () => {
    const value = newDomain.trim();
    if (!value) return;
    setError("");
    setDomainBusy(true);
    try {
      const res = await addTrustedDomain(value, user.email);
      setTrustedDomains(res.trusted_domains || []);
      setNewDomain("");
      toast.success(`${value.startsWith("@") ? value : "@" + value} added to trusted domains`);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add trusted domain");
    } finally {
      setDomainBusy(false);
    }
  };

  const handleRemoveDomain = async (domain) => {
    setError("");
    try {
      const res = await removeTrustedDomain(domain, user.email);
      setTrustedDomains(res.trusted_domains || []);
      toast.success(`${domain} removed`);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove trusted domain");
    }
  };

  const handleAddStaffDomain = async () => {
    const value = newStaffDomain.trim();
    if (!value) return;
    setError("");
    setStaffDomainBusy(true);
    try {
      const res = await addStaffDomain(value);
      setStaffDomains((s) => ({ ...s, extras: res.extras || [], effective: res.effective || [] }));
      setNewStaffDomain("");
      toast.success(`${value.startsWith("@") ? value : "@" + value} added to staff domains`);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add staff domain");
    } finally {
      setStaffDomainBusy(false);
    }
  };

  const handleRemoveStaffDomain = async (domain) => {
    setError("");
    try {
      const res = await removeStaffDomain(domain);
      setStaffDomains((s) => ({ ...s, extras: res.extras || [], effective: res.effective || [] }));
      toast.success(`${domain} removed`);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove staff domain");
    }
  };

  // ── Layout chrome ────────────────────────────────────────────────
  // The same body powers two surfaces:
  //   • mode="modal" (legacy)  → fixed overlay + narrow centered panel.
  //   • mode="page"             → routed `/admin/settings` page; full
  //     width, no backdrop, browser back / deep-link friendly.
  const isPage = mode === "page";
  const Outer = isPage
    ? ({ children }) => (
        <div className="min-h-[calc(100vh-3.5rem)] bg-slate-50" data-testid="admin-panel-page">
          <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">{children}</div>
        </div>
      )
    : ({ children }) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="admin-panel-overlay">
          <div className="absolute inset-0 bg-black/20" onClick={onClose} />
          <div className={`relative w-full ${tab === "shipping" ? "max-w-4xl" : "max-w-xl"} bg-white rounded-sm shadow-xl border border-slate-200 overflow-hidden max-h-[90vh] flex flex-col`}>
            {children}
          </div>
        </div>
      );

  return (
    <Outer>
      {/* Page-mode header (clean breadcrumb + title); modal-mode keeps
          the legacy X-button row. */}
      {isPage ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="admin-page-back"
                     onClick={() => onClose ? onClose() : window.history.back()}
                     className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 py-1 rounded-sm hover:bg-slate-100">
              <ArrowUUpLeft size={14} weight="bold" /> Back
            </button>
            <span className="text-slate-300">/</span>
            <ShieldCheck size={20} weight="bold" className="text-blue-700" />
            <h1 className="text-xl font-bold font-['Chivo'] text-slate-900">Admin Settings</h1>
          </div>
          <span className="text-[11px] uppercase tracking-wider text-slate-400 font-mono">{user?.email}</span>
        </div>
      ) : (
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} weight="bold" className="text-blue-700" />
            <h2 className="text-lg font-bold font-['Chivo']">Admin Settings</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>
      )}

        {/* DB fallback banner — admin override is down; app is running on env default */}
        {dbStatus?.fallback_used && (
          <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-start gap-2.5" data-testid="admin-db-fallback-banner">
            <Warning size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-xs leading-snug">
              <div className="font-bold text-amber-900">Currently using ENV default database</div>
              <div className="text-amber-800 mt-0.5">
                Admin-override host <span className="font-mono">{dbStatus?.primary?.host_prefix}</span> was unreachable.
                Running on default <span className="font-mono">{dbStatus?.fallback?.host_prefix}</span>.
                Fix the override in the <button onClick={() => setTab("database")} className="underline font-semibold">Database</button> tab.
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className={isPage ? "bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" : "contents"}>
        <div className="flex border-b border-slate-200 bg-slate-50">
          <button data-testid="admin-tab-admins" onClick={() => setTab("admins")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
              tab === "admins" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            Admins
          </button>
          <button data-testid="admin-tab-database" onClick={() => setTab("database")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "database" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <Database size={13} weight="bold" /> Database
          </button>
          <button data-testid="admin-tab-rights" onClick={() => setTab("rights")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "rights" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <Key size={13} weight="bold" /> Rights
          </button>
          <button data-testid="admin-tab-activity" onClick={() => setTab("activity")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "activity" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <Clock size={13} weight="bold" /> Activity
          </button>
          <button data-testid="admin-tab-email" onClick={() => setTab("email")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "email" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <EnvelopeSimple size={13} weight="bold" /> Email
          </button>
          <button data-testid="admin-tab-pwa" onClick={() => setTab("pwa")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "pwa" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <DeviceMobile size={13} weight="bold" /> PWA
          </button>
          <button data-testid="admin-tab-kb" onClick={() => setTab("kb")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "kb" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <BookOpen size={13} weight="bold" /> KB URLs
          </button>
          <button data-testid="admin-tab-evidence" onClick={() => setTab("evidence")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "evidence" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <Sparkle size={13} weight="bold" /> Evidence
          </button>
          {isSuperAdmin && (
            <button data-testid="admin-tab-shipping" onClick={() => setTab("shipping")}
              className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
                tab === "shipping" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}>
              <Truck size={13} weight="bold" /> Shipping
            </button>
          )}
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
        {tab === "admins" && (
          <>
            <p className="text-xs text-slate-500" data-testid="admin-domain-hint">
              {isSuperAdmin ? (
                <>
                  As a <b>Super-Admin</b>, you can promote any email to admin — domain restrictions are bypassed. Regular admins are still limited to <b>@aircompressor.com</b>, <b>@usaircompressor.com</b>, or <b>@aircompressorcanada.com</b>.
                </>
              ) : (
                <>
                  Only emails ending with <b>@aircompressor.com</b>, <b>@usaircompressor.com</b>, or <b>@aircompressorcanada.com</b> can be admins.
                </>
              )}
            </p>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
                <Warning size={14} weight="bold" /> {error}
              </div>
            )}

            {/* Add new admin */}
            <div className="flex gap-2">
              <Input
                data-testid="admin-add-email-input"
                placeholder={isSuperAdmin ? "any-email@example.com" : "email@usaircompressor.com"}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                className="flex-1 rounded-sm border-slate-300 h-9 text-sm"
              />
              <Button
                data-testid="admin-add-button"
                disabled={loading || !newEmail.trim()}
                onClick={handleAdd}
                className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold"
              >
                <Plus size={14} weight="bold" className="mr-1" /> Add
              </Button>
            </div>

            {/* Admin list */}
            <div className="space-y-1">
              {admins.map((email) => (
                <div key={email}
                  data-testid={`admin-row-${email}`}
                  className="flex items-center justify-between px-3 py-2 rounded-sm border border-slate-100 hover:bg-slate-50">
                  <span className="text-sm text-slate-800">{email}</span>
                  {email === protectedEmail ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                      Protected
                    </span>
                  ) : (
                    <button
                      data-testid={`admin-remove-${email}`}
                      onClick={() => handleRemove(email)}
                      className="w-7 h-7 flex items-center justify-center rounded-sm border border-red-200 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* iter150AZ.88 — Trusted-domain whitelist (super-admin only).
                Built-in env-baked domains render as protected; super-admin
                additions can be removed inline. */}
            {isSuperAdmin && (
              <div
                data-testid="trusted-domains-section"
                className="mt-6 pt-4 border-t border-slate-200 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Trusted email domains
                  </h3>
                  <span className="text-[10px] text-slate-400">
                    Super-Admin only
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  Regular admins can promote peers whose email ends in one of
                  these domains. Built-in domains are baked into the deploy
                  and cannot be removed from here.
                </p>
                <div className="flex gap-2">
                  <Input
                    data-testid="trusted-domain-input"
                    placeholder="@newco.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
                    className="flex-1 rounded-sm border-slate-300 h-9 text-sm"
                  />
                  <Button
                    data-testid="trusted-domain-add-button"
                    disabled={domainBusy || !newDomain.trim()}
                    onClick={handleAddDomain}
                    className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold"
                  >
                    <Plus size={14} weight="bold" className="mr-1" /> Add
                  </Button>
                </div>
                <div className="space-y-1">
                  {trustedDomains.map((d) => {
                    const isBuiltIn = trustedDomainsDefault.includes(d);
                    return (
                      <div
                        key={d}
                        data-testid={`trusted-domain-row-${d}`}
                        className="flex items-center justify-between px-3 py-2 rounded-sm border border-slate-100 hover:bg-slate-50"
                      >
                        <span className="text-sm font-mono text-slate-800">
                          {d}
                        </span>
                        {isBuiltIn ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                            Built-in
                          </span>
                        ) : (
                          <button
                            data-testid={`trusted-domain-remove-${d}`}
                            onClick={() => handleRemoveDomain(d)}
                            className="w-7 h-7 flex items-center justify-center rounded-sm border border-red-200 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                          >
                            <Trash size={13} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* iter200 — Main-company staff email domains (super-admin only).
                Anyone whose email ends in one of these is treated as internal
                staff: unrestricted compressor visibility, field-tools access,
                commissioning admin scope. Built-ins are baked into the deploy
                (backend/main_company.py) and shown as protected. */}
            {isSuperAdmin && (
              <div
                data-testid="staff-domains-section"
                className="mt-6 pt-4 border-t border-slate-200 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Staff email domains
                  </h3>
                  <span className="text-[10px] text-slate-400">Super-Admin only</span>
                </div>
                <p className="text-xs text-slate-500">
                  Users whose email ends in one of these domains are treated as
                  internal main-company staff — they see every compressor,
                  every customer, and every commissioning visit regardless of
                  dealer scope. Built-in domains are baked into the deploy.
                </p>
                <div className="flex gap-2">
                  <Input
                    data-testid="staff-domain-input"
                    placeholder="@newsistercompany.com"
                    value={newStaffDomain}
                    onChange={(e) => setNewStaffDomain(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddStaffDomain()}
                    className="flex-1 rounded-sm border-slate-300 h-9 text-sm"
                  />
                  <Button
                    data-testid="staff-domain-add-button"
                    disabled={staffDomainBusy || !newStaffDomain.trim()}
                    onClick={handleAddStaffDomain}
                    className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold"
                  >
                    <Plus size={14} weight="bold" className="mr-1" /> Add
                  </Button>
                </div>
                <div className="space-y-1">
                  {(staffDomains.effective || []).map((d) => {
                    const isBuiltIn = (staffDomains.built_in || []).includes(d);
                    return (
                      <div
                        key={d}
                        data-testid={`staff-domain-row-${d}`}
                        className="flex items-center justify-between px-3 py-2 rounded-sm border border-slate-100 hover:bg-slate-50"
                      >
                        <span className="text-sm font-mono text-slate-800">{d}</span>
                        {isBuiltIn ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                            Built-in
                          </span>
                        ) : (
                          <button
                            data-testid={`staff-domain-remove-${d}`}
                            onClick={() => handleRemoveStaffDomain(d)}
                            className="w-7 h-7 flex items-center justify-center rounded-sm border border-red-200 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                          >
                            <Trash size={13} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {tab === "database" && <DbCredentialsForm user={user} />}
        {tab === "rights" && <RightsPanel user={user} />}
        {tab === "activity" && <ActivityPanel user={user} />}
        {tab === "email" && <EmailPanel user={user} />}
        {tab === "pwa" && <PwaBrandingPanel user={user} />}

        {tab === "kb" && <KnowledgeBaseUrlsPanel user={user} />}

        {tab === "evidence" && <EvidenceLibraryPanel user={user} />}
        {tab === "shipping" && <ShippingVendorsPanel user={user} />}
        </div>

        {/* iter146 — Persistent admin footer with a quick "View all tours"
            link. Lives outside the scrollable tab body so super-admins can
            always reach it regardless of which tab they're looking at. */}
        <div className="px-6 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 text-[11px] text-slate-500">
          <span>Admin Settings · {TOURS.length} interactive tours available</span>
          <button
            data-testid="admin-footer-view-tours"
            onClick={() => setToursOpen(true)}
            className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 hover:underline font-semibold"
          >
            <BookOpen size={11} weight="bold" /> View all tours
          </button>
        </div>
        </div>{/* /isPage card wrapper */}

      <AllToursModal open={toursOpen} onClose={() => setToursOpen(false)} user={user} />
    </Outer>
  );
}

// iter150AZ.94 — Egress IP badge.
// Shows the server's outbound public IPv4 so an admin can whitelist it
// on the MariaDB host (cPanel "Remote MySQL" / firewall ACL) or any
// upstream API filtering by source IP. One-click copy + refresh.
function EgressIpBadge({ user }) {
  const [ip, setIp] = useState("");
  const [source, setSource] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = (refresh = false) => {
    // iter150AZ.106 — guard against the AdminPanel mounting before
    // ``user`` is hydrated. Without this, ``user.email`` resolves to
    // ``undefined`` and axios SKIPS that query param entirely (it does
    // not serialize ``undefined`` values) — the backend then returns
    // 422 "requester_email field required". Fail closed instead.
    const email = user?.email;
    if (!email) {
      setLoading(false);
      setRefreshing(false);
      setErr("Waiting for user session…");
      return;
    }
    setErr("");
    if (refresh) setRefreshing(true); else setLoading(true);
    fetchEgressIp(email, { refresh })
      .then((d) => {
        setIp(d.ip || "");
        setSource(d.source || "");
        setErr(d.error || "");
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to fetch egress IP"))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  // Re-fire load when ``user.email`` finally arrives (covers the
  // race where AdminPanel renders before the parent finishes
  // hydrating the auth session).
  useEffect(() => { load(false); }, [user?.email]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = async () => {
    if (!ip) return;
    try {
      await navigator.clipboard.writeText(ip);
      setCopied(true);
      toast.success(`Copied ${ip} to clipboard`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback for non-secure contexts.
      const el = document.createElement("textarea");
      el.value = ip;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); setCopied(true); toast.success(`Copied ${ip}`); }
      catch { toast.error("Copy failed — select manually"); }
      finally { document.body.removeChild(el); }
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div
      data-testid="admin-egress-ip-card"
      className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2.5"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Globe size={14} weight="bold" className="text-blue-700 shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 shrink-0">
            Server egress IP
          </span>
          {loading ? (
            <span className="text-xs text-slate-400" data-testid="admin-egress-ip-loading">Probing…</span>
          ) : ip ? (
            <button
              type="button"
              onClick={handleCopy}
              data-testid="admin-egress-ip-value"
              title="Click to copy"
              className="font-mono text-sm font-bold text-slate-900 bg-white border border-slate-300 rounded-sm px-2 py-0.5 hover:bg-blue-50 hover:border-blue-400 transition-colors cursor-pointer select-all"
            >
              {ip}
            </button>
          ) : (
            <span className="text-xs text-red-600" data-testid="admin-egress-ip-error">
              {err || "Unavailable"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {ip && (
            <Button
              type="button"
              onClick={handleCopy}
              data-testid="admin-egress-ip-copy"
              className={`h-7 px-2 text-[11px] font-semibold rounded-sm border transition-colors ${
                copied
                  ? "border-green-300 bg-green-50 text-green-700"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              {copied ? (
                <><CheckCircle size={12} weight="bold" className="mr-1" /> Copied</>
              ) : (
                <><Copy size={12} weight="bold" className="mr-1" /> Copy</>
              )}
            </Button>
          )}
          <Button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="admin-egress-ip-refresh"
            title="Re-probe (bust the 5-min cache)"
            className="h-7 w-7 p-0 flex items-center justify-center rounded-sm border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            <ArrowsClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
        Whitelist this IP on the MariaDB host (cPanel <span className="font-semibold">Remote MySQL</span> →
        Add Access Host) or any upstream API that filters by source IP.
        {source && <span className="text-slate-400"> · via {source}</span>}
      </p>
    </div>
  );
}


function DbCredentialsForm({ user }) {
  const [form, setForm] = useState(DB_DEFAULTS);
  const [source, setSource] = useState("env");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchDbConfig(user.email)
      .then((d) => {
        setForm((f) => ({
          host: d.host || f.host,
          database: d.database || f.database,
          user: d.user || f.user,
          // Don't pre-fill the actual password — show defaults only when source is env and unchanged.
          password: d.source === "mongo_override" ? "" : f.password,
          port: Number(d.port) || f.port || 3306,
        }));
        setSource(d.source || "env");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.email]);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setTestResult(null); };
  // Port has a numeric coercion + clamps to a sensible range.
  const setPort = (e) => {
    const raw = (e.target.value || "").replace(/[^0-9]/g, "");
    const n = raw === "" ? "" : Math.max(1, Math.min(65535, Number(raw)));
    setForm((f) => ({ ...f, port: n }));
    setTestResult(null);
  };

  const resetDefaults = () => {
    setForm(DB_DEFAULTS);
    setTestResult(null);
    setMsg("");
    setErr("");
  };

  const handleTest = async () => {
    setTesting(true); setErr(""); setMsg(""); setTestResult(null);
    try {
      const r = await testDbConfig({ ...form, port: Number(form.port) || 3306, requester_email: user.email });
      setTestResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    setSaving(true); setErr(""); setMsg(""); setTestResult(null);
    try {
      const r = await saveDbConfig({ ...form, port: Number(form.port) || 3306, requester_email: user.email });
      setSource(r.source);
      setMsg("Saved. New connections now use these credentials.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleRevert = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Revert to environment defaults and delete the stored override?")) return;
    setSaving(true); setErr(""); setMsg("");
    try {
      const r = await resetDbConfig(user.email);
      setSource(r.source);
      setMsg("Reverted to environment defaults.");
      // Reload to pick up env values
      const d = await fetchDbConfig(user.email);
      setForm({ host: d.host, database: d.database, user: d.user, password: "", port: Number(d.port) || 3306 });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Revert failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading current credentials…</div>;

  return (
    <div className="space-y-4" data-testid="db-config-form">
      <EgressIpBadge user={user} />

      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-slate-500">Active source:</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
          source === "mongo_override"
            ? "text-blue-700 bg-blue-50 border-blue-200"
            : "text-slate-600 bg-slate-50 border-slate-200"
        }`} data-testid="db-config-source">
          {source === "mongo_override" ? "Saved Override" : "Environment (.env)"}
        </span>
      </div>
      <p className="text-xs text-slate-500">
        Credentials are stored in MongoDB so they survive redeploys. If the deployed app shows "Cannot load data", update and save here — no redeploy required.
      </p>

      {err && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
          <Warning size={14} weight="bold" /> {err}
        </div>
      )}
      {msg && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-sm border border-green-200">
          <CheckCircle size={14} weight="bold" /> {msg}
        </div>
      )}
      {testResult && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-sm border ${
          testResult.ok
            ? "text-green-700 bg-green-50 border-green-200"
            : "text-red-700 bg-red-50 border-red-200"
        }`} data-testid="db-config-test-result">
          {testResult.ok ? <CheckCircle size={14} weight="bold" /> : <Warning size={14} weight="bold" />}
          {testResult.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {/* Host + Port on one row — Port is narrow since most cPanel
            users will keep 3306 or use a single forwarded port. */}
        <div className="grid grid-cols-[1fr_120px] gap-3">
          <Field label="Host / IP" testId="db-host" value={form.host} onChange={set("host")} placeholder={DB_DEFAULTS.host} />
          <Field label="Port" testId="db-port" value={form.port} onChange={setPort} placeholder="3306" inputMode="numeric" />
        </div>
        <Field label="Database" testId="db-name" value={form.database} onChange={set("database")} placeholder={DB_DEFAULTS.database} />
        <Field label="User" testId="db-user" value={form.user} onChange={set("user")} placeholder={DB_DEFAULTS.user} />
        <Field label="Password" testId="db-pass" value={form.password} onChange={set("password")} type="password" placeholder={source === "mongo_override" ? "(re-enter to change)" : DB_DEFAULTS.password} />
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
        <Button data-testid="db-test-button" type="button" onClick={handleTest} disabled={testing}
          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
          {testing ? "Testing…" : "Test Connection"}
        </Button>
        <Button data-testid="db-save-button" type="button" onClick={handleSave} disabled={saving || !form.password}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
          {saving ? "Saving…" : "Save Credentials"}
        </Button>
        <Button data-testid="db-defaults-button" type="button" onClick={resetDefaults}
          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
          <ArrowCounterClockwise size={13} weight="bold" className="mr-1" /> Pre-fill Defaults
        </Button>
        {source === "mongo_override" && (
          <Button data-testid="db-revert-button" type="button" onClick={handleRevert} disabled={saving}
            className="rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 h-9 px-3 text-sm font-semibold">
            Revert to .env
          </Button>
        )}
      </div>

      {/* iter150AZ.109 — DB Ops Gateway config, collapsed by default
          and sandwiched between the common DB credentials block above
          and the Files Base URL section below. */}
      <DbOpsConfigSection user={user} />

      {/* iter188 (PR 4) — DataAccess mode / status panel. Super-admin
          only; lets ops flip between direct / api / shadow without a
          restart, and shows per-method latency + shadow-mismatch
          counters. */}
      <DataAccessSection user={user} />

      <FilesBaseUrlSection user={user} />
      <DriveConfigSection user={user} />
    </div>
  );
}

// iter150AZ.109 — DB Ops Gateway configuration. Folded by default so
// the common DB credential controls above stay the visual focus.
// Secrets (API secret, client cert PEM, client key PEM) are
// encrypted server-side; the API never returns plaintext. The UI
// shows a SHA-256 fingerprint suffix so an admin rotating a value
// can confirm visually that the new blob took effect.
function DbOpsConfigSection({ user }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(false);
  // iter186-v10 — track which specific button is in-flight so only
  // that one shows its loading label; the rest stay readable but
  // disabled. Without this, hitting "Test connection" would also
  // change Save's label to "Saving…" which is obviously wrong.
  const [busyKind, setBusyKind] = useState("");
  const [status, setStatus]   = useState(null); // backend response
  const [err, setErr]         = useState("");
  const [msg, setMsg]         = useState("");
  const [testMsg, setTestMsg] = useState(null);
  // Form: empty string for secrets means "keep existing", per
  // backend "re-enter to change" semantics.
  const [form, setForm] = useState({
    enabled: false,
    base_url: "",
    key_id: "app-self",
    ttl_seconds: 3600,
    renew_interval_seconds: 1800,
    allow_on_db_error: true,
    api_secret: "",
    cert_pem: "",
    key_pem: "",
  });

  // Load when the user opens the section the first time (lazy fetch
  // keeps Admin Settings snappy if no-one ever opens it).
  useEffect(() => {
    if (!open || status) return;
    setLoading(true);
    fetchDbOpsConfig(user.email)
      .then((d) => {
        setStatus(d);
        setForm({
          enabled: !!d.enabled,
          base_url: d.base_url || "",
          key_id: d.key_id || "app-self",
          ttl_seconds: Number(d.ttl_seconds) || 3600,
          renew_interval_seconds: Number(d.renew_interval_seconds) || 1800,
          allow_on_db_error: !!d.allow_on_db_error,
          api_secret: "", cert_pem: "", key_pem: "",
        });
      })
      .catch((e) =>
        setErr(e?.response?.data?.detail || "Failed to load DB Ops config"))
      .finally(() => setLoading(false));
  }, [open, user.email, status]);

  const refresh = () => {
    setStatus(null);
    setOpen(true);
  };

  const set = (k) => (e) => {
    setForm((f) => ({
      ...f,
      [k]: e?.target ? (
        e.target.type === "checkbox" ? e.target.checked :
        e.target.type === "number"   ? Number(e.target.value || 0) :
                                       e.target.value
      ) : e,
    }));
    setMsg(""); setErr(""); setTestMsg(null);
  };

  const payload = () => ({
    requester_email: user.email,
    ...form,
    // Coerce ints once at the boundary so the backend always sees a
    // valid number even if the input was cleared and re-typed.
    ttl_seconds: Number(form.ttl_seconds) || 3600,
    renew_interval_seconds: Number(form.renew_interval_seconds) || 1800,
  });

  const onTest = async () => {
    setBusy(true); setBusyKind("validate"); setTestMsg(null); setErr("");
    try {
      const r = await testDbOpsConfig(payload());
      setTestMsg(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false); setBusyKind("");
    }
  };

  const onDiagnose = async () => {
    // iter186-v8 — live mTLS + HMAC round-trip against the gateway's
    // GET /v1/db-clients/self/diagnose endpoint. Surfaces the
    // gateway's verdict (egress IP, TTL, quota, etc.) so the admin
    // can confirm the credentials work BEFORE saving.
    setBusy(true); setBusyKind("diagnose"); setTestMsg(null); setErr("");
    try {
      const r = await diagnoseDbOpsConfig(payload());
      if (r?.ok) {
        const ip = r?.result?.client_ip || r?.result?.source_ip;
        const ttl = r?.result?.ttl_seconds_remaining ?? r?.result?.ttl_seconds;
        const detail = [
          ip && `egress=${ip}`,
          ttl != null && `ttl=${ttl}s`,
        ].filter(Boolean).join("  ");
        setTestMsg({
          ok: true,
          message: detail
            ? `Gateway OK — ${detail}`
            : (r.message || "Gateway OK"),
        });
      } else {
        const parts = [r?.message];
        if (r?.gateway_status) parts.push(`HTTP ${r.gateway_status}`);
        if (r?.stage) parts.push(`(${r.stage})`);
        setTestMsg({
          ok: false,
          message: parts.filter(Boolean).join(" "),
        });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Diagnose failed");
    } finally {
      setBusy(false); setBusyKind("");
    }
  };

  const onManualSelfAllow = async () => {
    // iter186-v9 — manually trigger the same POST /v1/db-clients/self/allow
    // the background renew task would call. Useful when the gateway
    // forgot us (firewall flush, NAT egress IP rotation, etc.) and
    // we don't want to wait for the next renew tick.
    setBusy(true); setBusyKind("self_allow"); setTestMsg(null); setErr("");
    try {
      const r = await manualSelfAllowDbOps(user.email);
      setTestMsg({
        ok: !!r?.ok,
        message: r?.message || (r?.ok ? "Self-allow OK" : "Self-allow failed"),
      });
      // Refresh full status so the last-event label updates from the
      // authoritative server side (it's already in r.last_event but
      // pulling the masked config too keeps sourceBadge / active in
      // sync if anything shifted server-side).
      try {
        const fresh = await fetchDbOpsConfig(user.email);
        setStatus(fresh);
      } catch { /* leave existing status — last_event still shows */ }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Self-allow failed");
    } finally {
      setBusy(false); setBusyKind("");
    }
  };

  const onSave = async () => {
    setBusy(true); setBusyKind("save"); setMsg(""); setErr(""); setTestMsg(null);
    try {
      const d = await saveDbOpsConfig(payload());
      setStatus(d);
      setForm((f) => ({ ...f, api_secret: "", cert_pem: "", key_pem: "" }));
      setMsg("DB Ops configuration saved.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false); setBusyKind("");
    }
  };

  const onRevert = async () => {
    if (!window.confirm("Revert DB Ops config to environment defaults? "
                         + "Any saved secrets will be removed.")) return;
    setBusy(true); setBusyKind("revert"); setMsg(""); setErr(""); setTestMsg(null);
    try {
      const d = await resetDbOpsConfig(user.email);
      setStatus(d);
      setForm((f) => ({
        ...f, enabled: !!d.enabled, base_url: d.base_url || "",
        key_id: d.key_id || "app-self",
        ttl_seconds: Number(d.ttl_seconds) || 3600,
        renew_interval_seconds: Number(d.renew_interval_seconds) || 1800,
        allow_on_db_error: !!d.allow_on_db_error,
        api_secret: "", cert_pem: "", key_pem: "",
      }));
      setMsg("Reverted to environment defaults.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Revert failed");
    } finally {
      setBusy(false); setBusyKind("");
    }
  };

  const onWipeSecret = (field) => () => {
    if (!window.confirm(`Wipe the saved ${field} value? This cannot be undone.`))
      return;
    setForm((f) => ({ ...f, [field]: "__CLEAR__" }));
    setMsg(`${field} marked for clearing — click Save to apply.`);
    setTestMsg(null);
  };

  const sourceBadge = status?.source === "mongo_override" ? (
    <span className="ml-2 inline-block bg-blue-100 text-blue-700 text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm tracking-wider"
          data-testid="dbops-source-badge">SAVED OVERRIDE</span>
  ) : status?.source === "env" ? (
    <span className="ml-2 inline-block bg-slate-200 text-slate-600 text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm tracking-wider"
          data-testid="dbops-source-badge">USING .ENV</span>
  ) : null;

  return (
    <div className="pt-4 border-t border-slate-200" data-testid="dbops-config-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center w-full text-left gap-2 group"
        data-testid="dbops-config-toggle"
      >
        {open ? <CaretDown size={14} weight="bold" className="text-slate-500" />
              : <CaretRight size={14} weight="bold" className="text-slate-500" />}
        <LockKey size={14} weight="bold" className="text-slate-500" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 group-hover:text-slate-700">
          DB Ops Allowlist Gateway
        </span>
        {status && sourceBadge}
        {status?.active && (
          <span className="ml-1 inline-block bg-green-100 text-green-700 text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm tracking-wider"
                data-testid="dbops-active-badge">ACTIVE</span>
        )}
        <span className="ml-auto text-[11px] text-slate-400 font-normal normal-case">
          {open ? "click to collapse" : "click to expand"}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid="dbops-config-body">
          <p className="text-xs text-slate-500 leading-relaxed">
            Manages the dynamic IP allowlist registration with the DB Ops
            Gateway (mTLS + HMAC-SHA256 signing). Sensitive fields are{" "}
            <strong>encrypted at rest in MongoDB</strong> with a key derived
            from <code>MONGO_URL</code> — they are never written in plaintext
            and never returned by the API. Leave a secret blank to keep the
            saved value; use the Wipe button to remove one.
          </p>

          {status?.last_event?.at && (
            <div data-testid="dbops-last-event"
                 className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5">
              <span className="font-semibold uppercase tracking-wider text-slate-500">
                Last self-allow:
              </span>
              <span data-testid="dbops-last-event-ok"
                    className={`inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${
                      status.last_event.ok
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}>
                {status.last_event.ok ? "OK" : "FAILED"}
              </span>
              <span data-testid="dbops-last-event-kind"
                    className="font-mono text-slate-500">
                {status.last_event.kind || "—"}
              </span>
              <span data-testid="dbops-last-event-at"
                    className="text-slate-500"
                    title={new Date(status.last_event.at).toString()}>
                {new Date(status.last_event.at).toLocaleString()}
              </span>
              {status.last_event.message && (
                <span data-testid="dbops-last-event-msg"
                      className="text-slate-500 truncate">
                  · {status.last_event.message}
                </span>
              )}
            </div>
          )}

          {loading && (
            <p className="text-xs text-slate-400" data-testid="dbops-loading">
              Loading current configuration…
            </p>
          )}

          {!loading && status && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={set("enabled")}
                    data-testid="dbops-enabled-toggle"
                  />
                  <span className="font-semibold">Enabled</span>
                  <span className="text-[10px] text-slate-400">
                    (sends self-allow at startup + on a renewal cycle)
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.allow_on_db_error}
                    onChange={set("allow_on_db_error")}
                    data-testid="dbops-allow-on-db-error"
                  />
                  <span>Re-allow on DB error</span>
                </label>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Base URL</Label>
                <Input
                  data-testid="dbops-base-url"
                  value={form.base_url}
                  onChange={set("base_url")}
                  placeholder="https://dbops.example.com"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Key ID</Label>
                  <Input
                    data-testid="dbops-key-id"
                    value={form.key_id}
                    onChange={set("key_id")}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">TTL (seconds)</Label>
                  <Input
                    type="number" min={60} max={86400}
                    data-testid="dbops-ttl"
                    value={form.ttl_seconds}
                    onChange={set("ttl_seconds")}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Renew interval (sec)</Label>
                  <Input
                    type="number" min={60} max={43200}
                    data-testid="dbops-renew"
                    value={form.renew_interval_seconds}
                    onChange={set("renew_interval_seconds")}
                  />
                </div>
              </div>

              {/* Sensitive fields — masked + fingerprinted */}
              <SecretField
                label="API secret"
                value={form.api_secret}
                onChange={set("api_secret")}
                isSet={!!status.api_secret_set}
                fingerprint={status.api_secret_fingerprint}
                onWipe={onWipeSecret("api_secret")}
                testid="dbops-api-secret"
                placeholder="(leave blank to keep saved value)"
              />

              <SecretField
                label="Client certificate PEM"
                value={form.cert_pem}
                onChange={set("cert_pem")}
                isSet={!!status.cert_pem_set}
                fingerprint={status.cert_pem_fingerprint}
                onWipe={onWipeSecret("cert_pem")}
                testid="dbops-cert-pem"
                placeholder="-----BEGIN CERTIFICATE-----…"
                multiline
              />

              <SecretField
                label="Client key PEM"
                value={form.key_pem}
                onChange={set("key_pem")}
                isSet={!!status.key_pem_set}
                fingerprint={status.key_pem_fingerprint}
                onWipe={onWipeSecret("key_pem")}
                testid="dbops-key-pem"
                placeholder="-----BEGIN PRIVATE KEY-----…"
                multiline
              />

              {status?.last_client_ip && (
                <p className="text-[11px] text-slate-500">
                  Last confirmed egress IP from gateway:{" "}
                  <code className="bg-slate-100 px-1 py-0.5 rounded">
                    {status.last_client_ip}
                  </code>
                </p>
              )}

              <div className="flex flex-wrap gap-2 items-center pt-2">
                <Button data-testid="dbops-test-btn" type="button"
                        onClick={onTest} disabled={busy}
                        className="rounded-sm border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
                  {busyKind === "validate" ? "Validating…" : "Validate"}
                </Button>
                <Button data-testid="dbops-diagnose-btn" type="button"
                        onClick={onDiagnose} disabled={busy}
                        title="Live mTLS+HMAC call to GET /v1/db-clients/self/diagnose on the gateway"
                        className="rounded-sm border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
                  {busyKind === "diagnose" ? "Testing…" : "Test connection"}
                </Button>
                <Button data-testid="dbops-self-allow-btn" type="button"
                        onClick={onManualSelfAllow} disabled={busy}
                        title="Trigger POST /v1/db-clients/self/allow now (same as the auto renew, but on demand)"
                        className="rounded-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 h-9 px-3 text-sm font-semibold">
                  {busyKind === "self_allow" ? "Allowing…" : "Self-allow now"}
                </Button>
                <Button data-testid="dbops-save-btn" type="button"
                        onClick={onSave} disabled={busy}
                        className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white h-9 px-3 text-sm font-semibold">
                  {busyKind === "save" ? "Saving…" : "Save"}
                </Button>
                <Button data-testid="dbops-refresh-btn" type="button"
                        onClick={refresh} disabled={busy}
                        className="rounded-sm border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
                  <ArrowsClockwise size={13} weight="bold" className="mr-1" />
                  Refresh
                </Button>
                {status?.source === "mongo_override" && (
                  <Button data-testid="dbops-revert-btn" type="button"
                          onClick={onRevert} disabled={busy}
                          className="rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 h-9 px-3 text-sm font-semibold">
                    {busyKind === "revert" ? "Reverting…" : "Revert to .env"}
                  </Button>
                )}
              </div>

              {testMsg && (
                <p data-testid="dbops-test-result"
                   className={`text-xs ${testMsg.ok ? "text-green-700" : "text-red-700"}`}>
                  {testMsg.ok ? "✓ " : "✗ "}{testMsg.message}
                </p>
              )}
              {msg && (
                <p data-testid="dbops-save-msg" className="text-xs text-green-700">{msg}</p>
              )}
              {err && (
                <p data-testid="dbops-save-err" className="text-xs text-red-700">{err}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Render a sensitive field — masked when set, wipeable, fingerprinted.
// ``value`` is the *user input* (empty = keep existing); ``isSet`` /
// ``fingerprint`` come from the server. ``multiline`` switches to a
// <textarea> for PEM blobs.
function SecretField({ label, value, onChange, isSet, fingerprint,
                      onWipe, testid, placeholder, multiline = false }) {
  const isClearing = value === "__CLEAR__";
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Label className="text-xs text-slate-500">{label}</Label>
        {isSet && !isClearing && (
          <span className="text-[10px] text-slate-400 font-mono"
                data-testid={`${testid}-fingerprint`}>
            sha256:{fingerprint} ··· saved
          </span>
        )}
        {isClearing && (
          <span className="text-[10px] text-red-600 font-bold uppercase"
                data-testid={`${testid}-clearing`}>
            will be wiped on save
          </span>
        )}
        {!isSet && !isClearing && (
          <span className="text-[10px] text-slate-400">(not set)</span>
        )}
      </div>
      {multiline ? (
        <textarea
          data-testid={testid}
          value={isClearing ? "" : value}
          onChange={onChange}
          placeholder={placeholder}
          rows={3}
          className="w-full text-xs font-mono border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
          disabled={isClearing}
        />
      ) : (
        <Input
          data-testid={testid}
          type="password"
          value={isClearing ? "" : value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={isClearing}
        />
      )}
      <div className="flex gap-2 mt-1">
        {isSet && !isClearing && (
          <button type="button" onClick={onWipe}
                  className="text-[10px] text-red-600 hover:text-red-800 underline"
                  data-testid={`${testid}-wipe`}>
            wipe saved value
          </button>
        )}
        {isClearing && (
          <button type="button"
                  onClick={() => onChange({ target: { value: "" } })}
                  className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                  data-testid={`${testid}-cancel-wipe`}>
            cancel wipe
          </button>
        )}
      </div>
    </div>
  );
}

// iter188 (PR 4) — DataAccess mode / observability panel.
//
// Direct = existing in-process SQL path (default).
// Api    = calls the co-located ``db_api_service`` sidecar.
// Shadow = both, direct is the source of truth, api runs in parallel
//          and every divergence is logged to the metrics feed.
//
// Ops flow: start on ``direct`` → deploy db_api_service → switch to
// ``shadow`` → watch shadow_mismatch counters for a canary window →
// switch to ``api`` once clean. Direct always stays available as a
// rollback surface via a one-click switch here.
function DataAccessSection({ user }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyKind, setBusyKind] = useState(null); // "refresh" | "switch"
  const [status, setStatus]   = useState(null);
  const [error, setError]     = useState(null);
  const [switchTo, setSwitchTo] = useState("direct");
  const [reason, setReason]   = useState("");

  // iter190 (PR 7) — Preview Comparison state.
  const [previewOpen, setPreviewOpen]   = useState(false);
  const [previewMethod, setPreviewMethod] = useState("resolve_user_scope");
  const [previewParams, setPreviewParams] = useState('{"email":"admin@example.com"}');
  const [previewRunning, setPreviewRunning] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError]   = useState(null);

  // iter188 (PR 4) — Match the rest of AdminPanel: super-admin status
  // is exposed as ``user.is_super_admin`` (boolean); ``user.role`` is
  // coarse ('admin'|'viewer'|'customer') and never equals 'super_admin'.
  const isSuperAdmin = Boolean(user?.is_super_admin);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await fetchDataAccessStatus();
      setStatus(s);
      setSwitchTo(s.active_mode || "direct");
    } catch (e) {
      setError(
        e?.response?.data?.detail
        || e?.message
        || "Failed to load DataAccess status"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !isSuperAdmin) return;
    load();
    const t = setInterval(load, 15000);   // light polling while open
    return () => clearInterval(t);
  }, [open, isSuperAdmin]);

  if (!isSuperAdmin) return null;

  const badgeForMode = (m) => {
    const map = {
      direct: "bg-slate-200 text-slate-700",
      api:    "bg-blue-100 text-blue-700",
      shadow: "bg-amber-100 text-amber-800",
    };
    return (
      <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm tracking-wider ${map[m] || "bg-slate-200 text-slate-700"}`}
            data-testid={`data-access-badge-${m}`}>
        {m}
      </span>
    );
  };

  const handleSwitch = async () => {
    if (!switchTo) return;
    if (switchTo === status?.active_mode && !reason) {
      toast.error("Pick a different mode or add a reason to force-record the switch.");
      return;
    }
    if (!window.confirm(
      `Switch DataAccess mode → ${switchTo.toUpperCase()}?\n\n` +
      (switchTo === "api"
        ? "The Emergent app will stop running SQL directly and call the DB API service instead. Rollback is one click.\n"
        : switchTo === "shadow"
        ? "Direct mode still serves users. API mode runs in parallel; divergences will appear in the counters below.\n"
        : "Restores in-process SQL. Use this to roll back after an API-mode incident.\n") +
      (reason ? `\nReason: ${reason}` : "")
    )) return;
    setBusyKind("switch");
    try {
      const resp = await setDataAccessMode({ mode: switchTo, reason });
      toast.success(`DataAccess mode: ${resp.old_mode} → ${resp.new_mode}`);
      setReason("");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
        || e?.message
        || "Mode switch failed"
      );
    } finally {
      setBusyKind(null);
    }
  };

  const activeMode = status?.active_mode || "direct";
  const runtimeOverride = status?.runtime_override;
  const envDefault = status?.env_default || "direct";
  const apiCfg = status?.db_api || {};
  const methods = status?.metrics?.methods || {};
  const events = status?.metrics?.events || [];
  const shadowMismatchCount = Object.entries(
    status?.metrics?.counters || {}
  ).filter(([k]) => k.startsWith("shadow_mismatch."))
   .reduce((s, [,v]) => s + v, 0);

  // iter190 (PR 7) — default params per method, so the operator can
  // hit "Run comparison" without typing JSON from scratch.
  const _defaultParamsFor = (m) => ({
    resolve_user_scope: '{"email":"admin@example.com"}',
    get_customer_summary: '{"customer_id":"REPLACE_ME"}',
    list_compressors: '{"pagination":{"page":1,"per_page":50},"scope":{}}',
    run_ai_tool: '{"tool":"search_parts","role":"admin","params":{"q":"filter"}}',
  }[m] || "{}");

  const handleRunPreview = async () => {
    setPreviewRunning(true);
    setPreviewResult(null);
    setPreviewError(null);
    let params;
    try {
      params = JSON.parse(previewParams || "{}");
    } catch (e) {
      setPreviewError("Params must be valid JSON: " + e.message);
      setPreviewRunning(false);
      return;
    }
    try {
      const res = await previewDataAccessComparison({
        method: previewMethod, params,
      });
      setPreviewResult(res);
    } catch (e) {
      setPreviewError(
        e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e?.message
        || "Preview comparison failed",
      );
    } finally {
      setPreviewRunning(false);
    }
  };

  return (
    <div className="pt-4 border-t border-slate-200" data-testid="data-access-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center w-full text-left gap-2 group"
        data-testid="data-access-toggle"
      >
        {open ? <CaretDown size={14} weight="bold" className="text-slate-500" />
              : <CaretRight size={14} weight="bold" className="text-slate-500" />}
        <ArrowsLeftRight size={14} weight="bold" className="text-slate-500" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 group-hover:text-slate-700">
          DataAccess Mode
        </span>
        {status && badgeForMode(activeMode)}
        {runtimeOverride && (
          <span className="inline-block bg-purple-100 text-purple-700 text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm tracking-wider"
                data-testid="data-access-override-badge">OVERRIDE</span>
        )}
        {shadowMismatchCount > 0 && (
          <span className="inline-block bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wider"
                data-testid="data-access-mismatch-badge">
            {shadowMismatchCount} SHADOW MISMATCH{shadowMismatchCount === 1 ? "" : "ES"}
          </span>
        )}
        <span className="ml-auto text-[11px] text-slate-400 font-normal normal-case">
          {open ? "click to collapse" : "click to expand"}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-4" data-testid="data-access-body">
          <p className="text-xs text-slate-500 leading-relaxed">
            Picks between the existing in-process SQL path (<code>direct</code>),
            the co-located <code>db_api_service</code> sidecar (<code>api</code>),
            and running both with direct as the source of truth (<code>shadow</code>).
            Every switch is audited under the operator&apos;s email in the event feed
            below. See <code>docs/db-access-audit.md</code> for the migration plan.
          </p>

          {loading && !status && (
            <p className="text-xs text-slate-400" data-testid="data-access-loading">
              Loading…
            </p>
          )}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2"
                 data-testid="data-access-error">
              {String(error)}
            </div>
          )}

          {status && (
            <>
              {/* Current state card */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Active</div>
                  <div className="mt-1" data-testid="data-access-active">
                    {badgeForMode(activeMode)}
                  </div>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Env default</div>
                  <div className="mt-1" data-testid="data-access-env-default">
                    {badgeForMode(envDefault)}
                  </div>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Runtime override</div>
                  <div className="mt-1 text-xs" data-testid="data-access-runtime-override">
                    {runtimeOverride
                      ? badgeForMode(runtimeOverride)
                      : <span className="text-slate-400 italic">none</span>}
                  </div>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">DB API endpoint</div>
                  <div className="mt-1 text-xs flex flex-wrap gap-1" data-testid="data-access-api-config">
                    <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm tracking-wider ${apiCfg.base_url_set ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                      URL {apiCfg.base_url_set ? "✓" : "✗"}
                    </span>
                    <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm tracking-wider ${apiCfg.token_set ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                      Token {apiCfg.token_set ? "✓" : "✗"}
                    </span>
                    <span className="inline-block bg-slate-200 text-slate-600 text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wider">
                      {apiCfg.timeout_s}s · {apiCfg.max_retries}× retry
                    </span>
                  </div>
                </div>
              </div>

              {/* Switch controls */}
              <div className="border border-slate-200 rounded-sm p-3 bg-white">
                <div className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">
                  Switch mode
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {["direct", "api", "shadow"].map((m) => (
                    <button key={m}
                      type="button"
                      data-testid={`data-access-switch-choice-${m}`}
                      onClick={() => setSwitchTo(m)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors
                        ${switchTo === m
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}
                        ${(m === "api" || m === "shadow") && (!apiCfg.base_url_set || !apiCfg.token_set)
                          ? "opacity-40 cursor-not-allowed" : ""}`}
                      disabled={(m === "api" || m === "shadow") && (!apiCfg.base_url_set || !apiCfg.token_set)}>
                      {m}
                    </button>
                  ))}
                  <Input
                    data-testid="data-access-switch-reason"
                    placeholder="Reason (optional but recommended)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="rounded-sm border-slate-300 text-sm h-9 flex-1 min-w-[240px]"
                  />
                  <Button
                    data-testid="data-access-apply-switch"
                    type="button"
                    onClick={handleSwitch}
                    disabled={busyKind === "switch" || loading}
                    className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-xs font-semibold">
                    {busyKind === "switch" ? "Switching…" : "Apply"}
                  </Button>
                  <Button
                    data-testid="data-access-refresh"
                    type="button"
                    onClick={load}
                    disabled={busyKind === "refresh" || loading}
                    className="rounded-sm border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 h-9 px-3 text-xs font-semibold">
                    <ArrowsClockwise size={13} weight="bold" className="mr-1" />
                    Refresh
                  </Button>
                </div>
                {(!apiCfg.base_url_set || !apiCfg.token_set) && (
                  <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
                    <Warning size={12} weight="bold" className="inline mr-1" />
                    API / Shadow modes are disabled — set <code>DB_API_BASE_URL</code>
                    and <code>DB_API_AUTH_TOKEN</code> in the backend .env first.
                  </p>
                )}
              </div>

              {/* Per-method metrics */}
              <div className="border border-slate-200 rounded-sm bg-white overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase text-slate-500 tracking-wider">
                  Per-method latency + calls (rolling 200-sample window)
                </div>
                {Object.keys(methods).length === 0 ? (
                  <p className="px-3 py-4 text-xs text-slate-400 italic" data-testid="data-access-methods-empty">
                    No calls served yet.
                  </p>
                ) : (
                  <table className="w-full text-xs" data-testid="data-access-methods-table">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-100">
                        <th className="px-3 py-2 font-semibold">Method</th>
                        <th className="px-3 py-2 font-semibold">Mode</th>
                        <th className="px-3 py-2 font-semibold text-right">Calls</th>
                        <th className="px-3 py-2 font-semibold text-right">Errors</th>
                        <th className="px-3 py-2 font-semibold text-right">p50 (ms)</th>
                        <th className="px-3 py-2 font-semibold text-right">p95 (ms)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(methods).flatMap(([method, byMode]) =>
                        Object.entries(byMode).map(([mode, s]) => (
                          <tr key={`${method}-${mode}`} className="border-b border-slate-50 last:border-b-0">
                            <td className="px-3 py-2 font-mono text-slate-700">{method}</td>
                            <td className="px-3 py-2">{badgeForMode(mode.replace("shadow-", ""))}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.count}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${s.errors > 0 ? "text-red-600 font-semibold" : ""}`}>
                              {s.errors}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.p50_ms}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.p95_ms}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {/* iter190 (PR 7) — Preview Comparison canary */}
              <div className="border border-slate-200 rounded-sm bg-white"
                   data-testid="data-access-preview-comparison-section">
                <button
                  type="button"
                  onClick={() => setPreviewOpen((v) => !v)}
                  className="w-full px-3 py-2 flex items-center gap-2 text-left"
                  data-testid="data-access-preview-toggle">
                  {previewOpen
                    ? <CaretDown size={12} weight="bold" className="text-slate-500" />
                    : <CaretRight size={12} weight="bold" className="text-slate-500" />}
                  <span className="text-xs font-bold uppercase text-slate-500 tracking-wider">
                    Preview comparison
                  </span>
                  <span className="text-[11px] text-slate-400 normal-case font-normal">
                    (run one request on BOTH modes; no global switch; read-only)
                  </span>
                </button>

                {previewOpen && (
                  <div className="px-3 pb-3 space-y-2"
                       data-testid="data-access-preview-body">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        data-testid="data-access-preview-method"
                        className="rounded-sm border-slate-300 text-xs h-8 px-2 bg-white"
                        value={previewMethod}
                        onChange={(e) => {
                          setPreviewMethod(e.target.value);
                          setPreviewParams(_defaultParamsFor(e.target.value));
                          setPreviewResult(null);
                          setPreviewError(null);
                        }}>
                        <option value="resolve_user_scope">resolve_user_scope</option>
                        <option value="get_customer_summary">get_customer_summary</option>
                        <option value="list_compressors">list_compressors</option>
                        <option value="run_ai_tool">run_ai_tool</option>
                      </select>
                      <Button
                        data-testid="data-access-preview-run"
                        type="button"
                        onClick={handleRunPreview}
                        disabled={previewRunning}
                        className="rounded-sm bg-slate-800 text-white hover:bg-slate-900 h-8 px-3 text-xs font-semibold">
                        {previewRunning ? "Running…" : "Run comparison"}
                      </Button>
                      {!apiCfg.base_url_set && (
                        <span className="text-[11px] text-amber-700">
                          <Warning size={11} weight="bold" className="inline mr-1" />
                          api side will be skipped — DB API URL/token not configured.
                        </span>
                      )}
                    </div>
                    <textarea
                      data-testid="data-access-preview-params"
                      value={previewParams}
                      onChange={(e) => setPreviewParams(e.target.value)}
                      placeholder="Method payload (JSON)"
                      rows={4}
                      className="w-full font-mono text-xs border border-slate-300 rounded-sm p-2"
                    />

                    {previewError && (
                      <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2"
                           data-testid="data-access-preview-error">
                        {String(previewError)}
                      </div>
                    )}
                    {previewResult && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs"
                           data-testid="data-access-preview-result">
                        {["direct", "api"].map((side) => {
                          const r = previewResult[side];
                          return (
                            <div key={side}
                                 className={`border rounded-sm p-2 ${r?.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-bold uppercase tracking-wider text-[10px]">
                                  {side} · {r?.ok ? "OK" : "ERR"}
                                </span>
                                <span className="tabular-nums text-slate-500">
                                  {r?.duration_ms}ms
                                </span>
                              </div>
                              <pre className="text-[11px] leading-relaxed overflow-auto max-h-56 whitespace-pre-wrap">
                                {r?.ok
                                  ? JSON.stringify(r.response, null, 2)
                                  : `${r?.error?.type}: ${r?.error?.message}`}
                              </pre>
                            </div>
                          );
                        })}
                        <div className="md:col-span-2 border border-slate-200 rounded-sm p-2 bg-slate-50">
                          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">
                            Diff
                          </div>
                          <pre className="text-[11px] leading-relaxed">
                            {previewResult.diff
                              ? JSON.stringify(previewResult.diff, null, 2)
                              : "— (skipped: one side did not run)"}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Event feed */}
              <div className="border border-slate-200 rounded-sm bg-white overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase text-slate-500 tracking-wider">
                  Recent events (mode switches · api errors · shadow mismatches)
                </div>
                {events.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-slate-400 italic" data-testid="data-access-events-empty">
                    No events recorded yet.
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="data-access-events-list">
                    {[...events].reverse().slice(0, 20).map((ev, i) => (
                      <li key={i} className="px-3 py-2 text-xs flex items-start gap-2">
                        <span className="tabular-nums text-slate-400 shrink-0 pt-0.5">
                          {new Date((ev.ts || 0) * 1000).toLocaleTimeString()}
                        </span>
                        {ev.kind === "mode_switch" ? (
                          <>
                            <span className="inline-block bg-purple-100 text-purple-700 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0">SWITCH</span>
                            <span className="text-slate-700">
                              <b>{ev.actor}</b>: {ev.old_mode} → <b>{ev.new_mode}</b>
                              {ev.reason ? ` — ${ev.reason}` : ""}
                            </span>
                          </>
                        ) : ev.kind === "shadow_mismatch" ? (
                          <>
                            <span className="inline-block bg-red-100 text-red-700 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0">MISMATCH</span>
                            <span className="text-slate-700">
                              <span className="font-mono">{ev.method}</span> — {ev.detail}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="inline-block bg-amber-100 text-amber-800 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0">API ERR</span>
                            <span className="text-slate-700">
                              <span className="font-mono">{ev.method}</span> — {ev.detail}
                            </span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}



function DriveConfigSection({ user }) {
  // iter177 — AppSheet pattern: ONE root folder. The DB already stores
  // each file path with its subfolder prefix (e.g.
  // ``Vendor_Shipments_Files/foo.xlsx``, ``Parts_Files/PB-115/manual.pdf``),
  // so a single root is all we need. Legacy per-kind folder IDs are
  // still kept in Mongo for backwards compatibility; if any are set
  // we show a one-line note so the operator can clean them up later.
  //
  // iter178 — added the Drive folder picker (native Google Picker
  // when picker creds are configured; in-app server-side browser
  // either way). Operator can now click a folder instead of pasting
  // a 33-char ID.
  const [form, setForm] = useState({
    api_key: "",
    root_folder_id: "",
    picker_oauth_client_id: "",
    picker_app_id: "",
  });
  const [legacy, setLegacy] = useState({ parts: "", pictures: "", vendor_shipments: "" });
  const [pickerAvailable, setPickerAvailable] = useState(false);
  // iter179 — auto-discovered subfolder map and the canonical list we
  // expect to find. ``registered`` is keyed by canonical name
  // (parts_files / pictures / vendor_shipments_files / vendor_shipments_images);
  // ``expected`` lets us render a checkmark next to each entry.
  const [registered, setRegistered] = useState({});
  const [expected, setExpected] = useState([]);
  const [rescanBusy, setRescanBusy] = useState(false);
  // iter180 — Audit modal toggle
  const [showAudit, setShowAudit] = useState(false);
  const [hasKey, setHasKey] = useState(false);
  const [maskedKey, setMaskedKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    getDriveConfig(user.email)
      .then((d) => {
        setForm({
          api_key: "",
          root_folder_id: d.root_folder_id || "",
          picker_oauth_client_id: d.picker_oauth_client_id || "",
          picker_app_id: d.picker_app_id || "",
        });
        setLegacy({
          parts: d.parts_folder_id || "",
          pictures: d.pictures_folder_id || "",
          vendor_shipments: d.vendor_shipments_folder_id || "",
        });
        setHasKey(Boolean(d.has_api_key));
        setMaskedKey(d.api_key_masked || "");
        setPickerAvailable(Boolean(d.picker_available));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.email]);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setTestResult(null); setMsg(""); setErr(""); };

  const test = async () => {
    setTesting(true); setErr(""); setMsg(""); setTestResult(null);
    try {
      const r = await testDriveConfig({ ...form, requester_email: user.email });
      setTestResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const r = await saveDriveConfig({ ...form, requester_email: user.email });
      // iter179 — the save endpoint runs auto-discovery and returns
      // the resolved subfolder map. Surface what was found right
      // away so the operator doesn't have to refresh.
      if (r?.registered_subfolders) {
        setRegistered(r.registered_subfolders || {});
      }
      const foundCount = Object.keys(r?.registered_subfolders || {}).length;
      const expectedCount = (expected || []).length || 4;
      setMsg(foundCount > 0
        ? `Drive config saved. Auto-registered ${foundCount} / ${expectedCount} canonical subfolder${foundCount === 1 ? "" : "s"}.`
        : "Drive config saved. File lookups will use the new key within 10 minutes (or restart backend to force).");
      if (form.api_key) { setHasKey(true); setMaskedKey(form.api_key.slice(0, 4) + "…" + form.api_key.slice(-4)); }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // iter179 — manual re-scan, for when the operator adds a new
  // subfolder in Drive and wants the app to pick it up without
  // re-saving everything.
  const rescan = async () => {
    setRescanBusy(true); setErr(""); setMsg("");
    try {
      const r = await rescanDriveSubfolders(user.email);
      setRegistered(r?.registered_subfolders || {});
      const foundCount = Object.keys(r?.registered_subfolders || {}).length;
      const missingCount = (r?.missing || []).length;
      setMsg(`Re-scan complete. Found ${foundCount} canonical subfolder${foundCount === 1 ? "" : "s"}`
              + (missingCount ? `, ${missingCount} missing.` : "."));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Re-scan failed");
    } finally { setRescanBusy(false); }
  };

  if (loading) return null;
  const hasLegacy = Boolean(legacy.parts || legacy.pictures || legacy.vendor_shipments);
  return (
    <div className="pt-4 border-t border-slate-200 space-y-2" data-testid="drive-config-section">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Google Drive</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
          hasKey ? "text-blue-700 bg-blue-50 border-blue-200" : "text-amber-700 bg-amber-50 border-amber-200"
        }`}>{hasKey ? `Key set (${maskedKey})` : "No API key"}</span>
      </div>

      {/* iter182 — Smart locator. Primary surface: one click to
          connect Drive (OAuth), one click to learn every file column.
          Operator never has to paste folder IDs again. */}
      <DriveSmartLocator user={user} />

      <details className="pt-3" data-testid="drive-manual-config">
        <summary className="cursor-pointer text-[11px] font-semibold text-slate-500 hover:text-slate-700 select-none">
          Manual config (advanced) <span className="font-normal text-slate-400">— API key + root folder, picker creds, subfolder registry, path audit</span>
        </summary>
        <div className="mt-3 space-y-2">
      <p className="text-[11px] text-slate-500">
        Paste the <b>one Drive root folder</b> that contains your <code className="font-mono bg-slate-100 px-1 rounded-sm">Parts_Files</code>, <code className="font-mono bg-slate-100 px-1 rounded-sm">Pictures</code>, <code className="font-mono bg-slate-100 px-1 rounded-sm">Vendor_Shipments_Files</code> and <code className="font-mono bg-slate-100 px-1 rounded-sm">Vendor_Shipments_Images</code> subfolders. The app resolves every file by walking the subfolders embedded in each DB path — same pattern AppSheet uses. Get a free API key at{" "}
        <a className="text-blue-700 underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">console.cloud.google.com/apis/credentials</a>{" "}
        and enable the <b>Google Drive API</b>. Real keys start with <code className="font-mono bg-slate-100 px-1 rounded-sm">AIza…</code> (39 chars).
      </p>

      <div className="grid grid-cols-1 gap-2">
        <div>
          <label className="text-[11px] font-semibold text-slate-600 mb-0.5 block">API Key {hasKey && <span className="text-[10px] text-slate-400 font-normal">(leave blank to keep existing)</span>}</label>
          <Input data-testid="drive-api-key" type="password" value={form.api_key} onChange={set("api_key")}
            placeholder={hasKey ? maskedKey : "AIza..."}
            className="h-9 text-sm font-mono rounded-sm border-slate-300" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-600 mb-0.5 block">
            Drive Root Folder ID
            <span className="ml-1 text-[10px] text-slate-400 font-normal">(contains Parts_Files / Pictures / Vendor_Shipments_Files / Vendor_Shipments_Images)</span>
          </label>
          <Input data-testid="drive-root-folder"
                   value={form.root_folder_id}
                   onChange={set("root_folder_id")}
                   placeholder="paste the ONE root folder ID — or use the buttons below to pick"
                   className="h-9 text-sm font-mono rounded-sm border-slate-300" />
          {/* iter178 — Drive folder picker. Lets the operator click a
              folder in their Drive instead of pasting the ID. */}
          <div className="mt-2">
            <DriveFolderPicker
              user={user}
              pickerAvailable={pickerAvailable && Boolean(form.picker_oauth_client_id)}
              hasApiKey={hasKey || Boolean(form.api_key)}
              onPick={(folder) => {
                setForm((f) => ({ ...f, root_folder_id: folder.id }));
                setMsg(`Picked folder: ${folder.name || folder.id}`);
                setTestResult(null);
              }}
            />
          </div>
          {/* iter179 — auto-registered canonical subfolder map. Shown
              whenever there's a root folder. ``expected`` is sourced
              from the API but we fall back to the canonical four
              names if it's empty (defensive). */}
          {form.root_folder_id && (hasKey || form.api_key) && (
            <div className="mt-3 border border-slate-200 rounded-sm bg-slate-50 px-2.5 py-2 space-y-1.5"
                  data-testid="drive-subfolder-registry">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Auto-registered subfolders
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <Button type="button"
                           data-testid="drive-subfolder-audit"
                           onClick={() => setShowAudit(true)}
                           disabled={!form.root_folder_id || !hasKey}
                           title="Walk every DB file path and report any that Drive can't find"
                           className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 h-7 px-2 text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50">
                    Audit paths
                  </Button>
                  <Button type="button"
                           data-testid="drive-subfolder-rescan"
                           onClick={rescan}
                           disabled={rescanBusy || !form.root_folder_id}
                           className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 h-7 px-2 text-[11px] font-semibold inline-flex items-center gap-1">
                    {rescanBusy ? "Scanning…" : "Re-scan"}
                  </Button>
                </div>
              </div>
              <ul className="space-y-0.5">
                {((expected && expected.length > 0)
                    ? expected
                    : ["parts_files", "pictures", "vendor_shipments_files", "vendor_shipments_images"]
                  ).map((name) => {
                  const fid = registered?.[name];
                  return (
                    <li key={name}
                          data-testid={`drive-subfolder-row-${name}`}
                          className="flex items-center gap-2 text-[12px]">
                      {fid ? (
                        <CheckCircle size={13} weight="fill" className="text-emerald-600 shrink-0" />
                      ) : (
                        <Warning size={13} weight="fill" className="text-amber-500 shrink-0" />
                      )}
                      <span className="font-mono text-slate-700 min-w-[14ch]">{name}</span>
                      {fid
                        ? <span className="font-mono text-slate-500 text-[11px] truncate">{fid}</span>
                        : <span className="text-amber-700 text-[11px]">not found under the root</span>}
                    </li>
                  );
                })}
              </ul>
              <p className="text-[10.5px] text-slate-500">
                Discovered automatically from the root folder — no per-folder paste needed. File resolution uses these IDs directly, skipping the per-request subfolder walk.
              </p>
            </div>
          )}
        </div>
        <details className="text-xs" data-testid="drive-picker-advanced">
          <summary className="cursor-pointer text-[11px] font-semibold text-slate-500 hover:text-slate-700 select-none">
            Google Picker credentials <span className="text-slate-400 font-normal">(optional — enables &quot;Pick from my Drive&quot;)</span>
          </summary>
          <div className="mt-2 space-y-2 pl-1">
            <p className="text-[11px] text-slate-500">
              In <a className="text-blue-700 underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console → Credentials</a>{" "}create an <b>OAuth 2.0 Client ID</b> (Application type: Web application) and add this app&apos;s origin to <b>Authorized JavaScript origins</b>. Also enable the <b>Google Picker API</b> under APIs &amp; Services → Library. Paste the client ID below; the project number is optional but recommended.
            </p>
            <div>
              <label className="text-[11px] font-semibold text-slate-600 mb-0.5 block">OAuth Client ID</label>
              <Input data-testid="drive-picker-client-id"
                       value={form.picker_oauth_client_id}
                       onChange={set("picker_oauth_client_id")}
                       placeholder="123…apps.googleusercontent.com"
                       className="h-9 text-sm font-mono rounded-sm border-slate-300" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-600 mb-0.5 block">
                Project Number <span className="text-[10px] text-slate-400 font-normal">(Picker App ID — optional)</span>
              </label>
              <Input data-testid="drive-picker-app-id"
                       value={form.picker_app_id}
                       onChange={set("picker_app_id")}
                       placeholder="numeric project number from Cloud Console"
                       className="h-9 text-sm font-mono rounded-sm border-slate-300" />
            </div>
          </div>
        </details>
        {hasLegacy && !form.root_folder_id && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 space-y-0.5"
                data-testid="drive-legacy-fallback-notice">
            <div className="font-bold">Using legacy per-kind folders until you fill the root.</div>
            {legacy.parts && <div>· Parts: <code className="font-mono">{legacy.parts}</code></div>}
            {legacy.pictures && <div>· Pictures: <code className="font-mono">{legacy.pictures}</code></div>}
            {legacy.vendor_shipments && <div>· Vendor Shipments: <code className="font-mono">{legacy.vendor_shipments}</code></div>}
            <div className="pt-0.5">Move all four subfolders under one parent in Drive, paste that parent&apos;s ID above, and the legacy fields become unused.</div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button type="button" data-testid="drive-test" onClick={test} disabled={testing || !form.api_key}
          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
          {testing ? "Testing…" : "Test Key + Folder"}
        </Button>
        <Button type="button" data-testid="drive-save" onClick={save} disabled={saving}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
          {saving ? "Saving…" : "Save Drive Config"}
        </Button>
      </div>

      {testResult && (
        <div className={`text-xs px-3 py-2 rounded-sm border ${
          testResult.ok ? "text-green-700 bg-green-50 border-green-200" : "text-red-700 bg-red-50 border-red-200"
        }`} data-testid="drive-test-result">
          <div className="font-semibold">{testResult.message}</div>
          {testResult.ok && testResult.subfolders?.length > 0 && (
            <>
              <div className="mt-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">Subfolders</div>
              <ul className="mt-0.5 list-disc pl-4 text-slate-700 text-[11px]">
                {testResult.subfolders.map((n) => (
                  <li key={n} className="truncate font-mono">{n}</li>
                ))}
              </ul>
            </>
          )}
          {testResult.ok && testResult.sample?.length > 0 && (
            <>
              <div className="mt-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">Loose files (first 10)</div>
              <ul className="mt-0.5 list-disc pl-4 text-slate-600 text-[11px]">
                {testResult.sample.map((n) => <li key={n} className="truncate">{n}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
      {msg && <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-sm border border-green-200">{msg}</div>}
      {err && <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-sm border border-red-200">{err}</div>}
      {showAudit && <DriveAuditModal user={user} onClose={() => setShowAudit(false)} />}
        </div>
      </details>
    </div>
  );
}

function FilesBaseUrlSection({ user }) {
  const [url, setUrlState] = useState("");
  const [source, setSource] = useState("unset");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    getFilesBaseUrl(user.email)
      .then((d) => { setUrlState(d.url || ""); setSource(d.source || "unset"); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.email]);

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      await setFilesBaseUrl(url.trim(), user.email);
      setSource("mongo_override");
      setMsg("Files base URL saved.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (loading) return null;
  return (
    <div className="pt-4 border-t border-slate-200 space-y-2" data-testid="files-base-url-section">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Files Base URL</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
          source === "mongo_override" ? "text-blue-700 bg-blue-50 border-blue-200"
          : source === "env" ? "text-slate-600 bg-slate-50 border-slate-200"
          : "text-amber-700 bg-amber-50 border-amber-200"
        }`}>{source === "mongo_override" ? "Saved" : source === "env" ? "Env" : "Unset"}</span>
      </div>
      <p className="text-[11px] text-slate-500">
        Base URL where Part_Files are hosted (e.g. <code className="font-mono bg-slate-100 px-1 rounded-sm">https://usaircompressor.com/files</code>). File paths stored in the DB are appended to this base when building download links.
      </p>
      <div className="flex gap-2">
        <Input data-testid="files-base-url-input" value={url} onChange={(e) => { setUrlState(e.target.value); setMsg(""); setErr(""); }}
          placeholder="https://example.com/files"
          className="flex-1 h-9 text-sm font-mono rounded-sm border-slate-300" />
        <Button type="button" data-testid="files-base-url-save" onClick={save} disabled={saving}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      {msg && <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-sm border border-green-200">{msg}</div>}
      {err && <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-sm border border-red-200">{err}</div>}
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", placeholder, inputMode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <Input data-testid={testId} type={type} inputMode={inputMode} value={value ?? ""} onChange={onChange} placeholder={placeholder}
        className="rounded-sm border-slate-300 h-9 text-sm font-mono" />
    </div>
  );
}


// ─── Rights (RBAC) tab ───────────────────────────────────────────
function RightsPanel({ user }) {
  const [me, setMe] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);   // {email, name, source_table, source_ref, existing_rights}
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const isSuperAdmin = Boolean(me?.is_super_admin);

  const loadAll = async () => {
    try {
      const [mePayload, perms, rolesPayload, usersPayload] = await Promise.all([
        fetchAccessMe(),
        fetchAccessPermissions(),
        fetchAccessRoles(),
        fetchAccessUsers(),
      ]);
      setMe(mePayload);
      setCatalog(perms.catalog || []);
      setRoles(rolesPayload.roles || []);
      setUsers(usersPayload.users || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // People search (debounced via key)
  useEffect(() => {
    let timer = setTimeout(async () => {
      try {
        const r = await searchAccessPeople({ q, limit: 15 });
        setPeople(r.results || []);
      } catch { setPeople([]); }
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const chooseExistingUser = (u) => {
    setSelected({
      email: u.email,
      name: u.display_name || u.email,
      source_table: u.source_table || "",
      source_ref: u.source_ref || "",
      existing_rights: u,
    });
    setMsg(""); setErr("");
  };

  const choosePerson = (p) => {
    setSelected({
      email: p.email,
      name: p.name || p.email,
      source_table: p.source_table,
      source_ref: p.source_ref || "",
      existing_rights: p.existing_rights || null,
    });
    setQ("");
    setPeople([]);
    setMsg(""); setErr("");
  };

  if (!me) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  if (!isSuperAdmin) {
    return (
      <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="rights-not-superadmin">
        <p className="font-semibold mb-1 flex items-center gap-1.5"><Warning size={14} weight="bold" className="text-amber-600" /> Super-admin only</p>
        <p className="text-xs text-slate-600">Only <b>bora@</b> or <b>deniz@</b> at <b>usaircompressor.com</b> / <b>aircompressorcanada.com</b> can manage rights.</p>
        <p className="mt-2 text-xs text-slate-500">Your current role: <b>{me.role || "—"}</b> · Permissions: {me.permissions.length}.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="rights-panel">
      {/* Rights Agent — natural-language duplicate detector */}
      <RightsAgent roles={roles} onRoleCreated={loadAll} />

      {/* Create / extend role template */}
      <RoleTemplateEditor roles={roles} catalog={catalog} onChanged={loadAll} />

      {/* Recent agent actions */}
      <AgentHistoryPanel />

      {/* People search */}
      <div>
        <h3 className="text-xs font-black uppercase tracking-[0.15em] text-slate-500 mb-2">Find a person</h3>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="rights-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search UsAirPeople, customer contacts, supplier contacts…"
            className="pl-9 rounded-sm border-slate-300 h-9 text-sm" />
        </div>
        {people.length > 0 && (
          <ul className="mt-2 border border-slate-200 rounded-sm bg-white divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="rights-people-results">
            {people.map((p) => {
              const label = p.source_table === "usair_people" ? "STAFF" : p.source_table === "customer_persons" ? "CUSTOMER" : "SUPPLIER";
              const tone = p.source_table === "usair_people" ? "bg-blue-50 text-blue-700 border-blue-200" : p.source_table === "customer_persons" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
              return (
                <li key={`${p.source_table}-${p.email}`}>
                  <button onClick={() => choosePerson(p)} data-testid={`rights-person-${p.email}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 text-sm">
                    <span className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-sm ${tone}`}>{label}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{p.name || p.email}</p>
                      <p className="text-[11px] text-slate-500 truncate">{p.email}{p.extras?.company ? ` · ${p.extras.company}` : ""}</p>
                    </div>
                    {p.existing_rights && <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">ROLE: {p.existing_rights.role || "—"}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Existing users */}
      <div>
        <h3 className="text-xs font-black uppercase tracking-[0.15em] text-slate-500 mb-2">Users with explicit rights ({users.length})</h3>
        {users.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No users yet. Find someone above to assign a role.</p>
        ) : (
          <ul className="border border-slate-200 rounded-sm bg-white divide-y divide-slate-100 max-h-60 overflow-y-auto" data-testid="rights-users-list">
            {users.map((u) => (
              <li key={u.email}>
                <button onClick={() => chooseExistingUser(u)} data-testid={`rights-user-${u.email}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 text-sm">
                  <UserCircle size={16} className="text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{u.display_name || u.email}</p>
                    <p className="text-[11px] text-slate-500 truncate">{u.email} · Updated {(u.updated_at || "").slice(0, 10)} by {u.updated_by || "—"}</p>
                  </div>
                  <span className="text-[9px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm">{u.role || "—"}</span>
                  <span className="text-[10px] font-mono text-slate-500">{(u.effective_permissions || []).length} perms</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editor */}
      {selected && (
        <RightsEditor
          key={`editor-${selected.email}`}
          subject={selected}
          roles={roles}
          catalog={catalog}
          saving={saving}
          onSave={async ({ role, granted, revoked }) => {
            setSaving(true); setErr(""); setMsg("");
            try {
              await upsertAccessUser({
                email: selected.email, role, granted, revoked,
                source_table: selected.source_table,
                source_ref: selected.source_ref,
                display_name: selected.name,
              });
              setMsg(`Saved rights for ${selected.email}`);
              await loadAll();
              setSelected((s) => s ? ({ ...s, existing_rights: { ...s.existing_rights, role, granted, revoked } }) : s);
            } catch (e) {
              setErr(e?.response?.data?.detail || "Save failed");
            } finally { setSaving(false); }
          }}
          onDelete={async () => {
            // eslint-disable-next-line no-alert
            if (!window.confirm(`Remove all explicit rights for ${selected.email}? They'll fall back to whatever the system grants by default.`)) return;
            setSaving(true); setErr(""); setMsg("");
            try {
              await deleteAccessUser(selected.email);
              setMsg(`Removed rights for ${selected.email}`);
              setSelected(null);
              await loadAll();
            } catch (e) {
              setErr(e?.response?.data?.detail || "Delete failed");
            } finally { setSaving(false); }
          }}
          onCancel={() => setSelected(null)}
        />
      )}

      {msg && <div className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-sm border border-green-200" data-testid="rights-msg">{msg}</div>}
      {err && <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200" data-testid="rights-err">{err}</div>}
    </div>
  );
}

function RightsEditor({ subject, roles, catalog, saving, onSave, onDelete, onCancel }) {  const initial = subject.existing_rights || {};
  const [role, setRole] = useState(initial.role || "");
  const [granted, setGranted] = useState(new Set(initial.granted || []));
  const [revoked, setRevoked] = useState(new Set(initial.revoked || []));

  const activeRole = roles.find((r) => r.role === role);
  const basePerms = new Set(activeRole?.permissions || []);

  const effectiveSet = new Set([...basePerms, ...granted]);
  for (const r of revoked) effectiveSet.delete(r);

  const togglePerm = (key) => {
    // Cycle: base → granted extra → revoked → base (if in base)
    // or base → granted → off (if not in base)
    const inBase = basePerms.has(key);
    const isGranted = granted.has(key);
    const isRevoked = revoked.has(key);
    const nextG = new Set(granted); const nextR = new Set(revoked);
    if (inBase) {
      if (!isRevoked) { nextR.add(key); nextG.delete(key); }
      else { nextR.delete(key); }
    } else {
      if (!isGranted) { nextG.add(key); nextR.delete(key); }
      else { nextG.delete(key); }
    }
    setGranted(nextG); setRevoked(nextR);
  };

  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="rights-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-800">{subject.name}</h3>
          <p className="text-[11px] text-slate-500 font-mono">{subject.email} · source: {subject.source_table || "—"}</p>
        </div>
        <button onClick={onCancel} data-testid="rights-editor-close" className="text-slate-400 hover:text-slate-700 text-xs">Close</button>
      </div>

      <div className="mb-3">
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Role</label>
        <select data-testid="rights-editor-role" value={role} onChange={(e) => setRole(e.target.value)}
          className="w-full rounded-sm border-slate-300 h-9 text-sm px-2">
          <option value="">— No role —</option>
          {roles.map((r) => <option key={r.role} value={r.role}>{r.label || r.role} ({r.permissions.length} perms)</option>)}
        </select>
      </div>

      {/* iter145 — One-click Make Test Admin toggle. Granting flips the
          three test_admin.* permissions on; revoking removes them.
          Operates purely on the local granted/revoked sets so the
          existing Save action persists the change atomically with any
          other matrix tweaks the super-admin made in this session. */}
      {(() => {
        const TEST_ADMIN_TRIPLE = ["test_admin.access", "test_admin.approve", "test_admin.manage_queue"];
        const allOn = TEST_ADMIN_TRIPLE.every((p) => granted.has(p) || basePerms.has(p));
        const toggleTestAdmin = () => {
          const nextG = new Set(granted);
          const nextR = new Set(revoked);
          if (allOn) {
            TEST_ADMIN_TRIPLE.forEach((p) => { nextG.delete(p); nextR.delete(p); });
          } else {
            TEST_ADMIN_TRIPLE.forEach((p) => { nextG.add(p); nextR.delete(p); });
          }
          setGranted(nextG); setRevoked(nextR);
        };
        return (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-sm bg-emerald-50 border border-emerald-200">
            <button type="button" onClick={toggleTestAdmin}
                    data-testid="rights-editor-make-test-admin"
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm ${allOn ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"} hover:opacity-90`}>
              {allOn ? "Remove Test Admin" : "Make Test Admin"}
            </button>
            <span className="text-[11px] text-emerald-900">
              {allOn
                ? "User has the Test Admin triple. Save to persist."
                : "Grant the test_admin.access / approve / manage_queue triple in one click."}
            </span>
          </div>
        );
      })()}

      {/* iter147 — One-click Make HR Admin toggle. Mirrors the test-admin
          shortcut above but flips the hr_admin.* triple. */}
      {(() => {
        const HR_ADMIN_TRIPLE = ["hr_admin.access", "hr_admin.manage_library", "hr_admin.publish"];
        const allOn = HR_ADMIN_TRIPLE.every((p) => granted.has(p) || basePerms.has(p));
        const toggleHrAdmin = () => {
          const nextG = new Set(granted);
          const nextR = new Set(revoked);
          if (allOn) {
            HR_ADMIN_TRIPLE.forEach((p) => { nextG.delete(p); nextR.delete(p); });
          } else {
            HR_ADMIN_TRIPLE.forEach((p) => { nextG.add(p); nextR.delete(p); });
          }
          setGranted(nextG); setRevoked(nextR);
        };
        return (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-sm bg-indigo-50 border border-indigo-200">
            <button type="button" onClick={toggleHrAdmin}
                    data-testid="rights-editor-make-hr-admin"
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm ${allOn ? "bg-rose-600 text-white" : "bg-indigo-600 text-white"} hover:opacity-90`}>
              {allOn ? "Remove HR Admin" : "Make HR Admin"}
            </button>
            <span className="text-[11px] text-indigo-900">
              {allOn
                ? "User has the HR Admin triple. Save to persist."
                : "Grant hr_admin.access / manage_library / publish in one click."}
            </span>
          </div>
        );
      })()}

      <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="rights-editor-matrix">
        {catalog.map((cat) => (
          <div key={cat.category}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-2">{cat.category}</p>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-sm bg-white">
              {cat.items.map((perm) => {
                const inBase = basePerms.has(perm.key);
                const isGranted = granted.has(perm.key);
                const isRevoked = revoked.has(perm.key);
                const isEffective = effectiveSet.has(perm.key);
                const state = inBase ? (isRevoked ? "revoked" : "base")
                  : (isGranted ? "granted" : "off");
                const stateClass = {
                  base: "bg-blue-50 border-blue-200 text-blue-700",
                  granted: "bg-emerald-50 border-emerald-200 text-emerald-700",
                  revoked: "bg-red-50 border-red-200 text-red-700",
                  off: "bg-slate-100 border-slate-200 text-slate-400",
                }[state];
                return (
                  <li key={perm.key} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <button onClick={() => togglePerm(perm.key)} data-testid={`rights-perm-${perm.key}`}
                      className={`text-[9px] font-bold border px-2 py-1 rounded-sm w-24 text-center ${stateClass}`}>
                      {state === "base" ? "FROM ROLE" : state === "granted" ? "+ GRANTED" : state === "revoked" ? "✕ REVOKED" : "— OFF"}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${isEffective ? "text-slate-800" : "text-slate-400"}`}>{perm.label}</p>
                      <p className="text-[10px] text-slate-400 font-mono truncate">{perm.key}</p>
                    </div>
                    {isEffective && <CheckCircle size={14} weight="fill" className="text-emerald-500" />}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button data-testid="rights-editor-save" disabled={saving}
          onClick={() => onSave({ role, granted: [...granted], revoked: [...revoked] })}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
          {saving ? "Saving…" : "Save rights"}
        </Button>
        {subject.existing_rights && (
          <Button data-testid="rights-editor-delete" disabled={saving} onClick={onDelete}
            className="rounded-sm bg-white border border-red-200 text-red-600 hover:bg-red-50 h-9 px-3 text-sm font-semibold">
            Remove explicit rights
          </Button>
        )}
        <div className="ml-auto text-[11px] text-slate-500">Effective permissions: <b>{effectiveSet.size}</b></div>
      </div>
    </div>
  );
}


// ─── Rights Agent (natural-language overlap detector) ────────────
function RightsAgent({ roles, onRoleCreated }) {
  const [prompt, setPrompt] = useState("");
  const [thinking, setThinking] = useState(false);
  const [response, setResponse] = useState(null);
  const [err, setErr] = useState("");
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(0);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);

  // Countdown timer — starts when `applied` arrives with an apply_id
  useEffect(() => {
    if (!applied?.apply_id || undone) { setUndoSecondsLeft(0); return; }
    const deadline = applied.undo_deadline_at
      ? Date.parse(applied.undo_deadline_at)
      : Date.now() + (applied.undo_window_seconds || 60) * 1000;
    const tick = () => {
      const remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setUndoSecondsLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [applied, undone]);

  const doUndo = async () => {
    if (!applied?.apply_id) return;
    setUndoing(true); setErr("");
    try {
      await advisorUndo(applied.apply_id);
      setUndone(true);
      onRoleCreated?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || e?.message || "Undo failed"));
    } finally { setUndoing(false); }
  };

  const run = async () => {
    if (!prompt.trim()) return;
    setThinking(true); setErr(""); setResponse(null); setApplied(null); setUndone(false);
    try {
      const r = await advisorChat(prompt.trim());
      setResponse(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Agent unavailable");
    } finally { setThinking(false); }
  };

  // ── "Apply" availability rules ────────────────────────────────
  //   create_permission  → need permission_key (any label); blocks on hard-conflict
  //   create_role        → need role_key and ≥1 permission; blocks on hard-conflict
  //   assign_permission  → need role_key + permission_key; always safe (noop on dup)
  const intent = response?.intent?.intent || "";
  const hardPermConflict = (response?.permission_conflicts || []).some((h) => (h.similarity || 0) >= 0.9);
  const hardRoleConflict = (response?.role_conflicts || []).some((h) => (h.similarity || 0) >= 0.9);
  const canApply = (() => {
    if (!response || applied) return false;
    if (intent === "create_permission") return Boolean(response.intent?.permission_key) && !hardPermConflict;
    if (intent === "create_role") return Boolean(response.intent?.role_key) && (response.intent?.permissions?.length || 0) > 0 && !hardRoleConflict;
    if (intent === "assign_permission") return Boolean(response.intent?.role_key && response.intent?.permission_key);
    return false;
  })();

  const apply = async (force = false) => {
    if (!response?.intent) return;
    const payload = {
      intent,
      permission_key: response.intent.permission_key || "",
      permission_label: response.intent.permission_label || "",
      permission_category: response.intent.permission_category || "Other",
      role_key: response.intent.role_key || "",
      role_label: response.intent.role_label || "",
      role_description: response.intent.rationale || "",
      permissions: response.intent.permissions || [],
      force,
    };
    setApplying(true); setErr(""); setUndone(false);
    try {
      const r = await advisorApply(payload);
      setApplied(r);
      onRoleCreated?.();
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail
        : (e?.response?.data?.detail?.message || e?.message || "Apply failed"));
    } finally { setApplying(false); }
  };

  const examples = [
    "Create a right to let staff look at customer data",
    "Add a role called Warehouse Clerk with view-only access to parts",
    "Grant can_create_quote to the sales_manager role",
  ];

  return (
    <div data-testid="rights-agent" className="border border-indigo-100 bg-indigo-50/60 rounded-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <Robot size={16} weight="bold" className="text-indigo-600" />
        <h3 className="text-sm font-bold text-indigo-900 font-['Chivo']">Access-control agent</h3>
        <span className="text-[9px] font-bold bg-indigo-600 text-white px-1.5 py-0.5 rounded-sm">AI</span>
      </div>
      <p className="text-[11px] text-slate-600 mb-3">
        Describe a permission or position in plain English. The agent checks for overlaps before creating anything new.
      </p>
      <div className="flex gap-2">
        <Input data-testid="rights-agent-prompt" value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !thinking) run(); }}
          placeholder="e.g. 'Create a Warehouse Clerk role'"
          className="rounded-sm border-indigo-200 h-9 text-sm" />
        <Button data-testid="rights-agent-send" onClick={run} disabled={thinking || !prompt.trim()}
          className="rounded-sm bg-indigo-600 text-white hover:bg-indigo-700 h-9 px-3 text-sm font-semibold">
          {thinking ? "Thinking…" : (<><PaperPlaneRight size={12} weight="bold" className="inline mr-1" />Ask</>)}
        </Button>
      </div>

      {!response && !thinking && (
        <div className="mt-2 flex flex-wrap gap-1">
          {examples.map((e) => (
            <button key={e} data-testid={`rights-agent-ex-${e.slice(0, 16).replace(/\s/g, '-')}`}
              onClick={() => setPrompt(e)}
              className="text-[10px] px-2 py-1 rounded-sm border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-100">
              {e}
            </button>
          ))}
        </div>
      )}

      {err && <div data-testid="rights-agent-err" className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{err}</div>}

      {response && (
        <div className="mt-3 space-y-3" data-testid="rights-agent-response">
          <div className="bg-white border border-indigo-200 rounded-sm p-3">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-1">
              <Sparkle size={10} weight="fill" />Agent verdict
            </div>
            <p className="text-sm text-slate-800">{(response.suggestion || "—").slice(0, 2000)}</p>
            <div className="mt-1.5 flex flex-wrap gap-1 text-[10px] font-mono text-slate-500">
              <span>intent: <b>{response.intent?.intent || "unknown"}</b></span>
              {response.intent?.role_key && <span>· role: <b className="text-blue-700">{response.intent.role_key}</b></span>}
              {response.intent?.permission_key && <span>· permission: <b className="text-emerald-700">{response.intent.permission_key}</b></span>}
              {Array.isArray(response.intent?.permissions) && response.intent.permissions.length > 0 && (
                <span>· +{response.intent.permissions.length} perms</span>
              )}
            </div>
            {applied ? (
              <div data-testid="rights-agent-applied" className="mt-3 space-y-2">
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-emerald-800 flex items-center gap-2">
                  <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                  <div className="flex-1">
                    <b>{undone ? "Undone" : "Applied"}</b> — {undone ? (
                      applied.applied === "create_permission" ? `Permission '${applied.permission?.key}' removed from catalog.`
                      : applied.applied === "create_role" ? `Role '${applied.role?.role}' reverted.`
                      : applied.applied === "assign_permission" ? `Reverted additions on '${applied.role?.role}'.`
                      : "Reverted."
                    ) : (
                      applied.applied === "create_permission"
                        ? `Permission '${applied.permission?.key}' added to catalog.`
                      : applied.applied === "create_role"
                        ? `Role '${applied.role?.role}' saved with ${applied.role?.permissions?.length || 0} permission(s).`
                      : applied.applied === "assign_permission"
                        ? `Added to '${applied.role?.role}': ${(applied.added_permissions || []).join(", ")}`
                      : applied.applied === "noop"
                        ? (applied.message || "No changes.")
                      : "Done."
                    )}
                  </div>
                  {!undone && applied.apply_id && undoSecondsLeft > 0 && (
                    <Button data-testid="rights-agent-undo" onClick={doUndo} disabled={undoing}
                      className="rounded-sm bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 h-7 px-2 text-[11px] font-bold flex items-center gap-1">
                      <ArrowUUpLeft size={11} weight="bold" />
                      {undoing ? "Undoing…" : `Undo · ${undoSecondsLeft}s`}
                    </Button>
                  )}
                </div>
                {!undone && applied.apply_id && undoSecondsLeft > 0 && (
                  <div className="relative h-[3px] w-full bg-emerald-100 rounded-sm overflow-hidden" aria-hidden="true">
                    <div data-testid="rights-agent-undo-bar" className="absolute inset-y-0 left-0 bg-emerald-500 transition-[width] duration-1000 linear"
                      style={{ width: `${(undoSecondsLeft / (applied.undo_window_seconds || 60)) * 100}%` }} />
                  </div>
                )}
                {!undone && applied.apply_id && undoSecondsLeft === 0 && (
                  <p className="text-[10px] text-slate-400 italic">Undo window expired — this change is now permanent.</p>
                )}
              </div>
            ) : canApply ? (
              <div className="mt-3 flex items-center gap-2">
                <Button data-testid="rights-agent-apply" onClick={() => apply(false)} disabled={applying}
                  className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 h-8 px-3 text-xs font-semibold">
                  {applying ? "Applying…" : "Apply this action"}
                </Button>
                <span className="text-[10px] text-slate-500">One-click commits the parsed intent.</span>
              </div>
            ) : response && (hardPermConflict || hardRoleConflict) ? (
              <div className="mt-3 flex items-center gap-2" data-testid="rights-agent-apply-blocked">
                <Button data-testid="rights-agent-apply-force" onClick={() => apply(true)} disabled={applying}
                  className="rounded-sm bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 h-8 px-3 text-xs font-semibold">
                  {applying ? "Applying…" : "Apply anyway (override duplicate)"}
                </Button>
                <span className="text-[10px] text-amber-600">Blocked — near-duplicate exists. Click only if truly distinct.</span>
              </div>
            ) : null}
          </div>

          {Array.isArray(response.permission_conflicts) && response.permission_conflicts.length > 0 && (
            <AgentConflictList
              title="Similar permissions that already exist"
              rows={response.permission_conflicts}
              keyFn={(r) => r.key}
              primaryFn={(r) => r.label}
              secondaryFn={(r) => `${r.key} · ${r.category}`}
            />
          )}
          {Array.isArray(response.role_conflicts) && response.role_conflicts.length > 0 && (
            <AgentConflictList
              title="Existing roles that overlap"
              rows={response.role_conflicts}
              keyFn={(r) => r.role}
              primaryFn={(r) => r.label || r.role}
              secondaryFn={(r) => `${r.permission_count} perms · ${r.shared_permissions?.length || 0} shared`}
            />
          )}
          {response.assignment_analysis && (
            <div className="bg-white border border-indigo-200 rounded-sm p-3 text-xs">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-1">
                <Sparkle size={10} weight="fill" />Assignment analysis
              </div>
              <ul className="space-y-1 font-mono">
                <li><span className="text-red-600 font-bold">Redundant:</span> {response.assignment_analysis.requested?.redundant?.join(", ") || "—"}</li>
                <li><span className="text-emerald-600 font-bold">Net new:</span> {response.assignment_analysis.requested?.net_new?.join(", ") || "—"}</li>
                <li><span className="text-amber-600 font-bold">Unknown (typo?):</span> {response.assignment_analysis.requested?.unknown?.join(", ") || "—"}</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AgentConflictList({ title, rows, keyFn, primaryFn, secondaryFn }) {
  return (
    <div className="bg-white border border-amber-200 rounded-sm p-3">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-2">
        <Warning size={11} weight="fill" />{title}
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <li key={keyFn(r) || i} className="flex items-center gap-2 py-2">
            <span className="text-[9px] font-mono text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">
              {Math.round((r.similarity || 0) * 100)}%
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{primaryFn(r)}</p>
              <p className="text-[11px] text-slate-500 truncate font-mono">{secondaryFn(r)}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ─── Role template editor (create / extend custom roles) ─────────
function RoleTemplateEditor({ roles, catalog, onChanged }) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState(new Set());
  const [cloneFrom, setCloneFrom] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [checking, setChecking] = useState(false);

  const chooseClone = (key) => {
    const r = roles.find((x) => x.role === key);
    setCloneFrom(key);
    if (r) setPermissions(new Set(r.permissions || []));
  };

  const check = async () => {
    if (!role.trim() && !label.trim()) return;
    setChecking(true);
    try {
      const r = await advisorCheckRole({ role: role.trim(), label: label.trim(), permissions: [...permissions] });
      setConflicts(r.matches || []);
    } catch { setConflicts([]); }
    finally { setChecking(false); }
  };

  const save = async () => {
    setErr(""); setMsg("");
    if (!role.trim()) { setErr("Role key is required (lowercase letters / digits / underscore)"); return; }
    setSaving(true);
    try {
      await upsertAccessRole({
        role: role.trim().toLowerCase(),
        label: label.trim() || role.trim(),
        description: description.trim(),
        permissions: [...permissions],
      });
      setMsg(`Role '${role}' saved with ${permissions.size} permission(s)`);
      setRole(""); setLabel(""); setDescription(""); setPermissions(new Set()); setCloneFrom(""); setConflicts([]);
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggle = (k) => {
    const n = new Set(permissions);
    if (n.has(k)) n.delete(k); else n.add(k);
    setPermissions(n);
  };

  return (
    <div data-testid="role-template-editor" className="border border-slate-200 rounded-sm bg-white">
      <button data-testid="role-template-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2">
          <Plus size={14} weight="bold" className="text-slate-500" />
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">Create / extend a role</h3>
          <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{roles.length} defined</span>
        </div>
        <span className="text-[10px] text-slate-400">{open ? "close" : "open"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Role key</label>
              <Input data-testid="role-template-key" value={role} onChange={(e) => setRole(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                placeholder="e.g. warehouse_clerk" className="rounded-sm border-slate-300 h-9 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Label</label>
              <Input data-testid="role-template-label" value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Warehouse Clerk" className="rounded-sm border-slate-300 h-9 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Description</label>
            <Input data-testid="role-template-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description shown in the role list" className="rounded-sm border-slate-300 h-9 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Clone permissions from…</label>
            <select data-testid="role-template-clone" value={cloneFrom} onChange={(e) => chooseClone(e.target.value)}
              className="w-full rounded-sm border-slate-300 h-9 text-sm px-2">
              <option value="">— Start blank —</option>
              {roles.map((r) => <option key={r.role} value={r.role}>{r.label || r.role} ({r.permissions.length} perms)</option>)}
            </select>
          </div>

          {/* Permissions matrix */}
          <div className="border border-slate-200 rounded-sm max-h-52 overflow-y-auto" data-testid="role-template-perms">
            {catalog.map((cat) => (
              <div key={cat.category}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-2 py-1 bg-slate-50 border-b border-slate-200">{cat.category}</p>
                <ul className="divide-y divide-slate-100">
                  {cat.items.map((p) => (
                    <li key={p.key} className="flex items-center gap-2 px-2 py-1 text-[12px]">
                      <input type="checkbox" data-testid={`role-template-perm-${p.key}`}
                        checked={permissions.has(p.key)} onChange={() => toggle(p.key)}
                        className="rounded border-slate-300" />
                      <span className="text-slate-700 flex-1">{p.label}</span>
                      <span className="text-[9px] text-slate-400 font-mono">{p.key}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button data-testid="role-template-check" onClick={check} disabled={checking}
              className="rounded-sm bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 h-9 px-3 text-xs font-semibold">
              {checking ? "Checking…" : "Check for overlaps"}
            </Button>
            <Button data-testid="role-template-save" onClick={save} disabled={saving}
              className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
              {saving ? "Saving…" : (role && roles.some((r) => r.role === role) ? "Update role" : "Create role")}
            </Button>
            <div className="ml-auto text-[11px] text-slate-500">{permissions.size} permission(s) selected</div>
          </div>

          {conflicts.length > 0 && (
            <AgentConflictList
              title="Existing roles that overlap"
              rows={conflicts}
              keyFn={(r) => r.role}
              primaryFn={(r) => r.label || r.role}
              secondaryFn={(r) => `${r.permission_count} perms · ${r.shared_permissions?.length || 0} shared`}
            />
          )}
          {msg && <div data-testid="role-template-msg" className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-sm border border-green-200">{msg}</div>}
          {err && <div data-testid="role-template-err" className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">{err}</div>}
        </div>
      )}
    </div>
  );
}




// ─── Agent history feed (inside Rights tab) ──────────────────────
function AgentHistoryPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await advisorHistory({ limit: 10 });
      setRows(r.events || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="agent-history-panel" className="border border-slate-200 rounded-sm bg-white">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200">
        <Robot size={14} weight="bold" className="text-slate-500" />
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">Recent agent actions</h3>
        <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{rows.length}</span>
        <button onClick={load} disabled={loading} data-testid="agent-history-refresh"
          className="ml-auto text-[10px] text-slate-400 hover:text-slate-700 underline">
          {loading ? "…" : "refresh"}
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-400 italic">No agent actions yet. Ask the agent above to create a permission or role.</p>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
          {rows.map((ev) => {
            const ago = ev.minutes_ago == null ? "—"
              : ev.minutes_ago < 1 ? "just now"
              : ev.minutes_ago < 60 ? `${ev.minutes_ago}m ago`
              : ev.minutes_ago < 1440 ? `${Math.floor(ev.minutes_ago / 60)}h ago`
              : `${Math.floor(ev.minutes_ago / 1440)}d ago`;
            const intentColor = ev.intent === "create_permission"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : ev.intent === "create_role"
                ? "bg-blue-50 text-blue-700 border-blue-200"
              : "bg-amber-50 text-amber-700 border-amber-200";
            return (
              <li key={ev.apply_id} data-testid={`agent-history-row-${ev.apply_id}`}
                className={`flex items-center gap-3 px-4 py-2 text-sm ${ev.undone ? "bg-slate-50/60" : ""}`}>
                <span className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-sm ${intentColor}`}>
                  {(ev.intent || "").replace("_", " ")}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${ev.undone ? "line-through text-slate-400" : "text-slate-800"}`}>
                    {ev.summary}
                  </p>
                  <p className="text-[10px] text-slate-500 truncate">
                    {ev.actor} · {ago}{ev.undone ? " · undone" : ""}
                  </p>
                </div>
                {ev.can_undo && !ev.undone && (
                  <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">
                    UNDO OPEN
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// ─── Activity tab (recent quote changes + agent actions) ─────────
function ActivityPanel({ user }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [window, setWindow] = useState(60 * 24); // 1 day default

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchRecentQuoteChanges({ limit: 40, since_minutes: window });
      setQuotes(r.events || []);
    } catch { setQuotes([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [window]);

  const WINDOWS = [
    { label: "1 hour",  mins: 60 },
    { label: "1 day",   mins: 60 * 24 },
    { label: "7 days",  mins: 60 * 24 * 7 },
    { label: "30 days", mins: 60 * 24 * 30 },
  ];

  return (
    <div className="space-y-5" data-testid="activity-panel">
      {/* Agent actions (reuses the Rights-tab component for consistency). */}
      <AgentHistoryPanel />

      {/* Recent quote changes */}
      <div className="border border-slate-200 rounded-sm bg-white" data-testid="recent-quote-changes">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200">
          <Receipt size={14} weight="bold" className="text-slate-500" />
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">Recent quote changes</h3>
          <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{quotes.length}</span>
          <select data-testid="recent-quote-window" value={window} onChange={(e) => setWindow(parseInt(e.target.value, 10))}
            className="ml-auto rounded-sm border-slate-300 h-7 text-xs px-2">
            {WINDOWS.map((w) => <option key={w.mins} value={w.mins}>{w.label}</option>)}
          </select>
          <button onClick={load} disabled={loading} data-testid="recent-quote-refresh"
            className="text-[10px] text-slate-400 hover:text-slate-700 underline">
            {loading ? "…" : "refresh"}
          </button>
        </div>
        {quotes.length === 0 ? (
          <p className="px-4 py-3 text-xs text-slate-400 italic">{loading ? "Loading…" : "No quote activity in the selected window."}</p>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {quotes.map((ev) => {
              const short = (ev.heading_id || "").startsWith("CART-") ? ev.heading_id.slice(5, 13) : (ev.heading_id || "").slice(0, 8);
              const entityColor = {
                quote: "bg-blue-50 text-blue-700 border-blue-200",
                quote_line: "bg-indigo-50 text-indigo-700 border-indigo-200",
                comment: "bg-amber-50 text-amber-700 border-amber-200",
                email: "bg-emerald-50 text-emerald-700 border-emerald-200",
                quote_pdf: "bg-purple-50 text-purple-700 border-purple-200",
              }[ev.entity] || "bg-slate-100 text-slate-600 border-slate-200";
              const ts = (ev.ts_utc || "").slice(5, 16).replace("T", " ");
              return (
                <li key={ev.id} data-testid={`recent-quote-row-${ev.id}`}
                  className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-slate-50">
                  <span className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-sm ${entityColor}`}>
                    {(ev.entity || "").replace("_", " ")}
                  </span>
                  <a href={`/quote/${encodeURIComponent(ev.heading_id)}`} target="_blank" rel="noreferrer"
                    data-testid={`recent-quote-link-${ev.heading_id}`}
                    className="text-[11px] font-mono text-blue-700 hover:underline whitespace-nowrap">
                    #{short}
                  </a>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 truncate">{ev.summary}</p>
                    <p className="text-[10px] text-slate-500 truncate">
                      {ev.quote_company || "—"} · {ev.actor_email || "—"} · {ev.action_type}
                      {ev.quote_status ? ` · ${ev.quote_status}` : ""}
                    </p>
                  </div>
                  <span className="text-[10px] font-mono text-slate-400 whitespace-nowrap">{ts}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}



// ─── Email routing & test-send panel ───
function EmailPanel({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [routes, setRoutes] = useState({});
  const [categories, setCategories] = useState([]);
  const [smtpReady, setSmtpReady] = useState(false);
  const [smtpUser, setSmtpUser] = useState("");
  const [emailProvider, setEmailProvider] = useState("smtp");
  const [providerReady, setProviderReady] = useState(false);
  const [providerSender, setProviderSender] = useState("");
  const [providerMissingMsg, setProviderMissingMsg] = useState("");
  const [testTo, setTestTo] = useState(user?.email || "");
  const [testBrand, setTestBrand] = useState("US");
  const [testCategory, setTestCategory] = useState("sales_usa");
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await fetchEmailRouting(user.email);
      setRoutes(d.routes || {});
      setCategories(d.categories || []);
      setSmtpReady(!!d.smtp_ready);
      setSmtpUser(d.smtp_user || "");
      setEmailProvider((d.email_provider || "smtp").toLowerCase());
      setProviderReady(!!d.provider_ready);
      setProviderSender(d.provider_sender || "");
      setProviderMissingMsg(d.provider_missing_msg || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load routing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setInfo("");
    try {
      const d = await saveEmailRouting({ requester_email: user.email, routes });
      setRoutes(d.routes || {});
      setInfo("Routing saved.");
      setTimeout(() => setInfo(""), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError("");
    setTestResult(null);
    try {
      const d = await sendTestEmail({
        requester_email: user.email,
        to_email: testTo,
        brand: testBrand,
        category: testCategory,
      });
      setTestResult(d);
    } catch (err) {
      setError(err.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-email-panel">
      <div
        className={`rounded-sm border px-3 py-2.5 text-xs leading-snug ${
          providerReady
            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : "bg-amber-50 border-amber-200 text-amber-900"
        }`}
        data-testid="admin-email-smtp-status"
        data-provider={emailProvider}
      >
        <div className="font-bold mb-0.5" data-testid="admin-email-provider-label">
          {emailProvider === "gmail_api"
            ? (providerReady ? "Gmail API configured" : "Gmail API not configured")
            : (providerReady ? "SMTP configured" : "SMTP not configured")}
        </div>
        <div>
          {providerReady
            ? (
                emailProvider === "gmail_api"
                  ? <>Sending from <b>{providerSender || "(GMAIL_SENDER_EMAIL not set)"}</b> via the Gmail API. If test sends fail with an OAuth error, re-run <code>python manage.py gmail-auth</code>.</>
                  : <>Sending from <b>{providerSender || smtpUser}</b>. If test sends fail with "Auth failed", the password in <code>.env</code> is not a valid 16-char Gmail App Password.</>
              )
            : (providerMissingMsg || (
                emailProvider === "gmail_api"
                  ? <>Set <code>GMAIL_SENDER_EMAIL</code> in backend/.env and run <code>python manage.py gmail-auth</code>, then restart the backend.</>
                  : <>Set <code>SMTP_USER</code> and <code>SMTP_PASSWORD</code> (16-char Gmail App Password) in backend/.env and restart the backend.</>
              ))
          }
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
          <Warning size={13} weight="bold" /> {error}
        </div>
      )}
      {info && (
        <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 px-3 py-2 rounded-sm border border-emerald-200">
          <CheckCircle size={13} weight="bold" /> {info}
        </div>
      )}

      <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/50 space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Send test email</div>
        <div className="flex flex-col gap-2">
          <Input
            data-testid="admin-email-test-to"
            placeholder="recipient@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="rounded-sm border-slate-300 h-9 text-sm"
          />
          <div className="flex gap-2">
            <select
              data-testid="admin-email-test-brand"
              value={testBrand}
              onChange={(e) => setTestBrand(e.target.value)}
              className="rounded-sm border border-slate-300 h-9 text-sm px-2 bg-white"
            >
              <option value="US">US</option>
              <option value="ACC">ACC (Canada)</option>
            </select>
            <select
              data-testid="admin-email-test-category"
              value={testCategory}
              onChange={(e) => setTestCategory(e.target.value)}
              className="rounded-sm border border-slate-300 h-9 text-sm px-2 bg-white flex-1"
            >
              {categories.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <Button
            data-testid="admin-email-test-send"
            disabled={testing || !testTo.trim()}
            onClick={handleTest}
            className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 text-sm font-semibold"
          >
            <PaperPlaneRight size={13} weight="bold" className="mr-1.5" />
            {testing ? "Sending…" : "Send test email"}
          </Button>
        </div>

        {testResult && (
          <div
            data-testid="admin-email-test-result"
            className={`text-xs leading-snug rounded-sm border px-3 py-2 mt-2 ${
              testResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-red-50 border-red-200 text-red-900"
            }`}
          >
            <div className="font-bold">
              {testResult.ok ? "Sent" : testResult.mocked ? "Skipped (mocked)" : "Failed"}
            </div>
            <div className="mt-1">
              <b>From:</b> {testResult.from_name} &lt;{testResult.from_email}&gt;<br/>
              <b>Reply-To:</b> {testResult.reply_to}
            </div>
            {testResult.message && (
              <div className="mt-1 break-all"><b>Details:</b> {testResult.message}</div>
            )}
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-sm">
        <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-700">
          Default email recipients & routing
        </div>
        <p className="px-3 pt-2 text-[11px] text-slate-500 leading-snug">
          <b>Customer / Dealer / Vendor</b> rows are the <b>Reply-To</b> header used when a guest triggers an email — for logged-in users, Reply-To is always their own address.
          <br />
          <b>Internal</b> rows are the actual recipients of automated workflows (engineering review of a signed commissioning, ops archive cc on every signed PDF, price-list-download notifications).
        </p>
        {loading ? (
          <div className="p-3 text-xs text-slate-400">Loading…</div>
        ) : (
          <div className="p-3 space-y-3">
            {["customer", "dealer", "vendor", "internal"].map((aud) => {
              const rows = categories.filter((c) => (c.audience || "customer") === aud);
              if (!rows.length) return null;
              const groupLabel = {
                customer: "Customer-facing (Reply-To)",
                dealer: "Dealer-facing (Reply-To)",
                vendor: "Vendor-facing (Reply-To)",
                internal: "Internal automated workflows",
              }[aud];
              return (
                <div key={aud} className="space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100 pb-1">
                    {groupLabel}
                  </div>
                  {rows.map((c) => (
                    <div key={c.key} className="flex flex-col sm:flex-row sm:items-start gap-1.5">
                      <label className="text-xs font-semibold text-slate-700 sm:w-44 shrink-0 sm:pt-1.5">
                        {c.label}
                        {c.description && (
                          <div className="text-[10px] font-normal text-slate-400 leading-tight mt-0.5">
                            {c.description}
                          </div>
                        )}
                      </label>
                      <Input
                        data-testid={`admin-email-route-${c.key}`}
                        type="email"
                        value={routes[c.key] || ""}
                        onChange={(e) => setRoutes({ ...routes, [c.key]: e.target.value })}
                        placeholder="inbox@example.com"
                        className="rounded-sm border-slate-300 h-8 text-xs flex-1"
                      />
                    </div>
                  ))}
                </div>
              );
            })}
            <div className="pt-2">
              <Button
                data-testid="admin-email-save-routing"
                disabled={saving}
                onClick={handleSave}
                className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 h-9 text-sm font-semibold"
              >
                <CheckCircle size={13} weight="bold" className="mr-1.5" />
                {saving ? "Saving…" : "Save routing"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// iter136.28 — PWA branding tab. Super-admin only.
//   * Edits ``title`` / ``short_name`` / ``description`` / ``theme_color``.
//   * Single drag-and-drop upload field — server auto-generates the
//     192/512 ``any`` and maskable PNGs from one source image.
//   * Live preview shows tab title, install card, Android adaptive
//     mask (circle clip), and iOS rounded-square so the super admin
//     sees what each surface will render before saving.
//   * Reset-to-factory wipes the override and restores the static
//     bundled defaults.
function PwaBrandingPanel({ user }) {
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const reload = () => {
    setError("");
    fetchPwaBranding()
      .then((b) => {
        setState(b);
        setDraft({
          title: b.title || "",
          short_name: b.short_name || "",
          description: b.description || "",
          theme_color: b.theme_color || "#ed1b24",
          company_name: b.company_name || "",
        });
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message || "Failed to load branding"));
  };
  useEffect(() => { reload(); }, []);

  if (!isSuperAdmin) {
    return (
      <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
        Only super admins can edit the PWA title and icon.
      </div>
    );
  }
  if (!state) {
    return <div className="text-xs text-slate-500">Loading branding…</div>;
  }

  const dirty =
    draft.title !== state.title ||
    draft.short_name !== state.short_name ||
    draft.description !== state.description ||
    draft.theme_color !== state.theme_color ||
    (draft.company_name || "") !== (state.company_name || "");

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const next = await updatePwaBranding(draft);
      setState(next);
      refreshBranding(next);
      window.dispatchEvent(new Event("usair:branding-changed"));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Image exceeds 5 MB limit");
      return;
    }
    setUploading(true); setError("");
    try {
      const next = await uploadPwaIcon(file);
      setState(next);
      refreshBranding(next);
      window.dispatchEvent(new Event("usair:branding-changed"));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to upload icon");
    } finally {
      setUploading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset PWA title and icon back to the bundled defaults? This cannot be undone.")) return;
    setResetting(true); setError("");
    try {
      const next = await resetPwaBranding();
      setState(next);
      refreshBranding(next);
      window.dispatchEvent(new Event("usair:branding-changed"));
      setDraft({
        title: next.title || "",
        short_name: next.short_name || "",
        description: next.description || "",
        theme_color: next.theme_color || "#ed1b24",
        company_name: next.company_name || "",
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  const iconUrl = state.icons?.["any-512"] || "/icon-512.png";
  const maskableUrl = state.icons?.["maskable-512"] || "/icon-maskable-512.png";

  return (
    <div className="space-y-5" data-testid="pwa-branding-panel">
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
          <Warning size={14} weight="bold" /> {error}
        </div>
      )}

      <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 space-y-1">
        <div><b>How this works.</b> Changes apply immediately to <i>browser</i> tabs after one refresh. Already-installed home-screen apps cache the icon + title at install time — users with the old PWA installed must remove and reinstall it from the home screen to pick up new branding.</div>
        {state.has_overrides && state.updated_at ? (
          <div className="text-[11px] text-slate-500">Last updated by <span className="font-mono">{state.updated_by}</span> at {new Date(state.updated_at).toLocaleString()}.</div>
        ) : (
          <div className="text-[11px] text-slate-500 italic">Currently using bundled defaults — no override set.</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="rounded-sm border border-emerald-200 bg-emerald-50/60 p-3">
            <label className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Company brand name</label>
            <Input
              data-testid="pwa-branding-company-name"
              value={draft.company_name}
              onChange={(e) => setDraft({ ...draft, company_name: e.target.value })}
              maxLength={80}
              className="rounded-sm border-emerald-300 h-9 text-sm mt-1 bg-white"
              placeholder="e.g. Kressor Compressor"
            />
            <div className="text-[10px] text-emerald-700 mt-1">
              Rebrands the whole app. When set, this name replaces every
              &ldquo;US Air Compressor&rdquo; / &ldquo;Air Compressor Canada&rdquo;
              reference across the UI, emails, PDFs and the AI assistant.
              Leave blank to keep the original per-region names. · {draft.company_name.length}/80
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">App title (browser tab)</label>
            <Input
              data-testid="pwa-branding-title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              maxLength={120}
              className="rounded-sm border-slate-300 h-9 text-sm mt-1"
              placeholder="Air Compressor Canada"
            />
            <div className="text-[10px] text-slate-400 mt-0.5">Browser tab + Android install splash · {draft.title.length}/120</div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Home-screen label (short)</label>
            <Input
              data-testid="pwa-branding-short-name"
              value={draft.short_name}
              onChange={(e) => setDraft({ ...draft, short_name: e.target.value })}
              maxLength={60}
              className="rounded-sm border-slate-300 h-9 text-sm mt-1"
              placeholder="ACC"
            />
            <div className="text-[10px] text-slate-400 mt-0.5">Shown under the icon on iOS / Android. Aim for ≤ 12 chars · {draft.short_name.length}/60</div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Description</label>
            <textarea
              data-testid="pwa-branding-description"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              maxLength={500}
              rows={3}
              className="w-full rounded-sm border border-slate-300 text-sm p-2 mt-1 resize-y"
              placeholder="One-sentence summary shown in the install prompt."
            />
            <div className="text-[10px] text-slate-400 mt-0.5">Shown in install prompts · {draft.description.length}/500</div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Theme colour</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                data-testid="pwa-branding-theme-color-picker"
                value={draft.theme_color}
                onChange={(e) => setDraft({ ...draft, theme_color: e.target.value })}
                className="w-10 h-9 rounded-sm border border-slate-300 cursor-pointer"
              />
              <Input
                data-testid="pwa-branding-theme-color-text"
                value={draft.theme_color}
                onChange={(e) => setDraft({ ...draft, theme_color: e.target.value.toLowerCase() })}
                maxLength={7}
                pattern="^#[0-9a-fA-F]{6}$"
                className="rounded-sm border-slate-300 h-9 text-sm font-mono w-28"
              />
              <span className="text-[10px] text-slate-400">Status bar + splash background</span>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              data-testid="pwa-branding-save"
              disabled={!dirty || saving}
              onClick={handleSave}
              className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 h-9 px-4 text-sm font-semibold"
            >
              <CheckCircle size={13} weight="bold" className="mr-1.5" />
              {saving ? "Saving…" : "Save"}
            </Button>
            {dirty && (
              <button
                data-testid="pwa-branding-discard"
                onClick={() => setDraft({
                  title: state.title || "",
                  short_name: state.short_name || "",
                  description: state.description || "",
                  theme_color: state.theme_color || "#ed1b24",
                })}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Discard
              </button>
            )}
            <span className="text-[10px] text-slate-400 ml-auto">Icon uploads save instantly — only the text fields above need this button.</span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Preview</div>

          <div className="border border-slate-200 rounded-sm bg-slate-100 p-2">
            <div className="text-[10px] text-slate-500 mb-1">Browser tab</div>
            <div className="bg-white rounded-t-md px-2 py-1.5 inline-flex items-center gap-2 max-w-full border border-slate-200" style={{ minWidth: 200 }}>
              <img src={iconUrl} alt="" className="w-4 h-4" />
              <span className="text-xs text-slate-700 truncate">{draft.title || state.title}</span>
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm bg-slate-50 p-3">
            <div className="text-[10px] text-slate-500 mb-2">Install prompt (Android)</div>
            <div className="bg-white rounded-md p-3 flex items-center gap-3 border border-slate-200">
              <img src={iconUrl} alt="" className="w-12 h-12 rounded-md flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{draft.title || state.title}</div>
                <div className="text-[11px] text-slate-500 line-clamp-2">{draft.description || state.description}</div>
              </div>
              <button className="text-[11px] font-bold text-blue-700 px-2 py-1 rounded-sm border border-blue-200 flex-shrink-0">Install</button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm bg-slate-50 p-3">
            <div className="text-[10px] text-slate-500 mb-2">Home-screen tile</div>
            <div className="grid grid-cols-2 gap-3 items-start">
              <div className="text-center">
                <div
                  className="mx-auto rounded-full overflow-hidden bg-white shadow"
                  style={{ width: 72, height: 72 }}
                  title="Android adaptive mask (circle)"
                >
                  <img src={maskableUrl} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="text-[10px] text-slate-700 mt-1.5 truncate font-medium">{draft.short_name || state.short_name}</div>
                <div className="text-[9px] text-slate-400">Android (circle mask)</div>
              </div>
              <div className="text-center">
                <div
                  className="mx-auto overflow-hidden bg-white shadow"
                  style={{ width: 72, height: 72, borderRadius: 16 }}
                  title="iOS rounded square"
                >
                  <img src={iconUrl} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="text-[10px] text-slate-700 mt-1.5 truncate font-medium">{draft.short_name || state.short_name}</div>
                <div className="text-[9px] text-slate-400">iOS (rounded square)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-sm p-4 bg-slate-50 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-bold text-slate-800">Replace icon</div>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">Saves instantly</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Upload one image (PNG, JPG, or WEBP, ≤ 5 MB). Server auto-pads non-square uploads, samples the background colour from the corners, and generates the four PWA sizes (192 / 512, regular + maskable safe-zone for Android adaptive masks). Applies the moment the upload finishes — no separate save needed.
              <br /><span className="text-slate-400">If you don't upload a logo, the app auto-creates one from the first letter of your brand name (in your theme colour) whenever you change the brand.</span>
            </div>
          </div>
        </div>
        <label className="block">
          <input
            type="file"
            data-testid="pwa-branding-icon-upload"
            accept="image/png,image/jpeg,image/webp"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) handleUpload(f);
            }}
            className="hidden"
          />
          <span className={`inline-flex items-center gap-2 px-3 h-9 rounded-sm text-sm font-semibold cursor-pointer ${uploading ? "bg-slate-300 text-slate-500" : "bg-blue-700 text-white hover:bg-blue-800"}`}>
            <Upload size={14} weight="bold" />
            {uploading ? "Uploading…" : "Choose image…"}
          </span>
        </label>
      </div>

      <div className="flex items-center justify-end pt-2 border-t border-slate-100">
        <button
          data-testid="pwa-branding-reset"
          disabled={!state.has_overrides || resetting}
          onClick={handleReset}
          className="text-xs text-red-600 hover:text-red-800 underline flex items-center gap-1.5 disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
        >
          <ArrowsClockwise size={12} weight="bold" />
          {resetting ? "Resetting…" : "Reset to bundled defaults"}
        </button>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────
// iter137.4 — Knowledge Base URLs panel (super-admin only).
// Lets the super-admin define the list of source URLs (web pages and
// PDFs) the warranty voice-analysis AI will consult per brand.
// Storage only in this iteration; the AI consumer is Phase 2.
// ─────────────────────────────────────────────────────────────────

function KnowledgeBaseUrlsPanel({ user }) {
  const [items, setItems] = useState({ US: { urls: [] }, ACC: { urls: [] } });
  const [validKinds, setValidKinds] = useState(["web", "pdf", "auto"]);
  const [validBrands, setValidBrands] = useState(["US", "ACC"]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const isSuper = !!(user?.is_super_admin);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/kb-urls")
      .then((r) => {
        setItems(r.data.items || { US: { urls: [] }, ACC: { urls: [] } });
        setValidKinds(r.data.valid_kinds || ["web", "pdf", "auto"]);
        setValidBrands(r.data.valid_brands || ["US", "ACC"]);
      })
      .catch(() => toast.error("Could not load Knowledge Base URLs"))
      .finally(() => setLoading(false));
  }, []);

  const setBrandUrls = (brand, urls) => {
    setItems((it) => ({ ...it, [brand]: { ...(it[brand] || {}), urls } }));
  };

  const save = async (brand) => {
    setSaving((s) => ({ ...s, [brand]: true }));
    try {
      const urls = (items[brand]?.urls || []).filter((u) => (u.url || "").trim());
      const { data } = await api.put("/admin/kb-urls", { brand, urls });
      setItems((it) => ({ ...it, [brand]: { ...(it[brand] || {}), urls: data.urls } }));
      toast.success(`Saved ${data.count} URL${data.count === 1 ? "" : "s"} for ${brand}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Save failed for ${brand}`);
    } finally {
      setSaving((s) => ({ ...s, [brand]: false }));
    }
  };

  if (loading) return <div className="text-xs text-slate-400 p-3">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-kb-urls-panel">
      <p className="text-xs text-slate-500 leading-snug">
        These URLs feed the warranty-claim voice-analysis AI. When a customer records a problem description,
        the AI will reference these sources to suggest possible causes &amp; troubleshooting steps.
        Mix web pages and PDFs — set <b>kind</b> to <i>auto</i> to let the system decide based on the file extension.
        {!isSuper && <span className="block mt-1 text-amber-700 font-semibold">View-only — only super-admins can save changes.</span>}
      </p>
      {validBrands.map((brand) => (
        <BrandUrlList
          key={brand}
          brand={brand}
          urls={items[brand]?.urls || []}
          onChange={(u) => setBrandUrls(brand, u)}
          onSave={() => save(brand)}
          saving={!!saving[brand]}
          updatedAt={items[brand]?.updated_at}
          updatedBy={items[brand]?.updated_by}
          validKinds={validKinds}
          readOnly={!isSuper}
        />
      ))}
    </div>
  );
}

function BrandUrlList({ brand, urls, onChange, onSave, saving, updatedAt, updatedBy, validKinds, readOnly }) {
  const _companyName = useBranding().company_name || "";
  const update = (idx, patch) => {
    const next = urls.map((u, i) => (i === idx ? { ...u, ...patch } : u));
    onChange(next);
  };
  const remove = (idx) => onChange(urls.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= urls.length) return;
    const next = urls.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const add = () => onChange([...urls, { url: "", label: "", kind: "auto" }]);

  return (
    <div className="border border-slate-200 rounded-sm">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
          {_companyName || (brand === "US" ? "US Air Compressor" : "Air Compressor Canada")} ({brand}) · {urls.length} URL{urls.length === 1 ? "" : "s"}
        </div>
        {updatedAt && (
          <div className="text-[10px] text-slate-400">
            Last saved {new Date(updatedAt).toLocaleString()} by {updatedBy || "—"}
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        {urls.length === 0 && (
          <div className="text-xs text-slate-400 italic py-2">No URLs configured yet.</div>
        )}
        {urls.map((u, i) => (
          <div
            key={i}
            data-testid={`admin-kb-url-row-${brand}-${i}`}
            className="grid grid-cols-12 gap-1.5 items-center"
          >
            <Input
              data-testid={`admin-kb-url-input-${brand}-${i}`}
              type="url"
              placeholder="https://example.com/manual.pdf"
              value={u.url || ""}
              onChange={(e) => update(i, { url: e.target.value })}
              disabled={readOnly}
              className="col-span-6 rounded-sm border-slate-300 h-8 text-xs"
            />
            <Input
              data-testid={`admin-kb-url-label-${brand}-${i}`}
              type="text"
              placeholder="Label (optional)"
              value={u.label || ""}
              onChange={(e) => update(i, { label: e.target.value })}
              disabled={readOnly}
              className="col-span-3 rounded-sm border-slate-300 h-8 text-xs"
            />
            <select
              data-testid={`admin-kb-url-kind-${brand}-${i}`}
              value={u.kind || "auto"}
              onChange={(e) => update(i, { kind: e.target.value })}
              disabled={readOnly}
              className="col-span-1 rounded-sm border border-slate-300 h-8 text-[11px] bg-white"
            >
              {validKinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <div className="col-span-2 flex items-center gap-1 justify-end">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={readOnly || i === 0}
                className="p-1 rounded-sm hover:bg-slate-100 disabled:opacity-30"
                title="Move up"
              ><ArrowUp size={12} weight="bold" /></button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={readOnly || i === urls.length - 1}
                className="p-1 rounded-sm hover:bg-slate-100 disabled:opacity-30"
                title="Move down"
              ><ArrowDown size={12} weight="bold" /></button>
              <button
                type="button"
                data-testid={`admin-kb-url-delete-${brand}-${i}`}
                onClick={() => remove(i)}
                disabled={readOnly}
                className="p-1 rounded-sm hover:bg-rose-50 text-rose-600 disabled:opacity-30"
                title="Remove"
              ><Trash size={12} weight="bold" /></button>
            </div>
          </div>
        ))}
        {!readOnly && (
          <div className="flex items-center justify-between pt-2">
            <Button
              data-testid={`admin-kb-add-${brand}`}
              onClick={add}
              variant="outline"
              className="rounded-sm h-8 text-xs"
            >
              <Plus size={12} weight="bold" className="mr-1" /> Add URL
            </Button>
            <Button
              data-testid={`admin-kb-save-${brand}`}
              onClick={onSave}
              disabled={saving}
              className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 h-8 text-xs"
            >
              <CheckCircle size={12} weight="bold" className="mr-1" />
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── iter137.5 — Supporting-evidence library admin panel ───────────
// Super-admins curate keyword-tagged reference photos / videos / text /
// PDFs / external links. The /api/warranty/claims/{id}/evidence-suggest
// endpoint scores claims' findings_text against these keywords and
// surfaces matches during the supporting-evidence step.

function EvidenceLibraryPanel({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isSuper = !!(user?.is_super_admin);

  useEffect(() => {
    setLoading(true);
    api.get("/warranty/admin/evidence-library")
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Could not load Evidence library"))
      .finally(() => setLoading(false));
  }, []);

  const update = (idx, patch) => {
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const add = () => setItems([...items, { title: "", description: "", keywords: [], url: "", media_type: "text" }]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = items
        .filter((it) => (it.title || "").trim())
        .map((it) => ({
          title: it.title,
          description: it.description || "",
          keywords: typeof it.keywords === "string"
            ? it.keywords.split(",").map((k) => k.trim()).filter(Boolean)
            : (it.keywords || []),
          url: it.url || null,
          media_type: it.media_type || "text",
        }));
      const r = await api.put("/warranty/admin/evidence-library", { items: cleaned });
      toast.success(`Saved ${r.data.count} entr${r.data.count === 1 ? "y" : "ies"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-xs text-slate-400 p-3">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-evidence-library-panel">
      <p className="text-xs text-slate-500 leading-snug">
        Curate reference evidence (photos, videos, text, links) tagged with keywords.
        When a customer fills in their findings text on a warranty claim, the system
        scores the text against these keywords and surfaces matching entries on the
        <i> supporting-evidence </i> step.
        {!isSuper && <span className="block mt-1 text-amber-700 font-semibold">View-only — only super-admins can save changes.</span>}
      </p>
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-slate-400 italic py-2">No entries yet.</div>
        )}
        {items.map((it, i) => (
          <div key={i} data-testid={`admin-evidence-row-${i}`}
            className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50">
            <div className="grid grid-cols-12 gap-1.5">
              <Input
                placeholder="Title"
                value={it.title || ""}
                onChange={(e) => update(i, { title: e.target.value })}
                disabled={!isSuper}
                data-testid={`admin-evidence-title-${i}`}
                className="col-span-7 rounded-sm border-slate-300 h-8 text-xs"
              />
              <select
                value={it.media_type || "text"}
                onChange={(e) => update(i, { media_type: e.target.value })}
                disabled={!isSuper}
                data-testid={`admin-evidence-type-${i}`}
                className="col-span-3 rounded-sm border border-slate-300 h-8 text-[11px] bg-white"
              >
                <option value="text">text</option>
                <option value="image">image</option>
                <option value="video">video</option>
                <option value="pdf">pdf</option>
                <option value="link">link</option>
              </select>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={!isSuper}
                data-testid={`admin-evidence-delete-${i}`}
                className="col-span-2 rounded-sm hover:bg-rose-50 text-rose-600 disabled:opacity-30 inline-flex items-center justify-center"
                title="Remove"
              ><Trash size={12} weight="bold" /></button>
            </div>
            <Input
              placeholder="Optional URL (https://…)"
              value={it.url || ""}
              onChange={(e) => update(i, { url: e.target.value })}
              disabled={!isSuper}
              data-testid={`admin-evidence-url-${i}`}
              className="rounded-sm border-slate-300 h-8 text-xs"
            />
            <Input
              placeholder="Keywords (comma-separated, e.g. overheat, airend, oil)"
              value={Array.isArray(it.keywords) ? (it.keywords || []).join(", ") : (it.keywords || "")}
              onChange={(e) => update(i, { keywords: e.target.value })}
              disabled={!isSuper}
              data-testid={`admin-evidence-keywords-${i}`}
              className="rounded-sm border-slate-300 h-8 text-xs"
            />
            <textarea
              placeholder="Description (what the customer should look for)"
              value={it.description || ""}
              onChange={(e) => update(i, { description: e.target.value })}
              disabled={!isSuper}
              data-testid={`admin-evidence-desc-${i}`}
              rows={2}
              className="w-full rounded-sm border border-slate-300 text-xs p-2 disabled:bg-slate-100"
            />
          </div>
        ))}
      </div>
      {isSuper && (
        <div className="flex items-center justify-between pt-2">
          <Button
            data-testid="admin-evidence-add"
            onClick={add}
            variant="outline"
            className="rounded-sm h-8 text-xs"
          >
            <Plus size={12} weight="bold" className="mr-1" /> Add entry
          </Button>
          <Button
            data-testid="admin-evidence-save"
            onClick={save}
            disabled={saving}
            className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 h-8 text-xs"
          >
            <CheckCircle size={12} weight="bold" className="mr-1" />
            {saving ? "Saving…" : "Save library"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── ShippingVendorsPanel ───────────────────────────────────────────
// Super-admin runtime credential editor for the direct-vendor
// shipping integration (iter150AZ.132). Shows each vendor with its
// configuration source (Mongo / env / unconfigured), lets the
// super-admin rotate credentials, and reports exactly what the live
// provider probe verified.
function ShippingVendorsPanel({ user }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [flagEnabled, setFlagEnabled] = useState(false);
  const [adapters, setAdapters] = useState({});
  const [editing, setEditing] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [editMode, setEditMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [showSecret, setShowSecret] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [health, creds] = await Promise.all([
        fetchShippingHealth().catch((e) => {
          if (e.response?.status === 503) return { enabled: false };
          throw e;
        }),
        fetchShippingCredentials().catch(() => ({})),
      ]);
      setFlagEnabled(!!health?.enabled);
      setAdapters(health?.adapters || {});
      setStatus(creds || {});
    } catch (e) {
      toast.error(`Failed to load shipping vendors: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (vendorKey, vendor) => {
    setEditing(vendorKey);
    setEditFields(Object.fromEntries(
      vendor.expected_fields.map((f) => [f, ""])
    ));
    setEditMode(vendor.mode || (vendor.modes && vendor.modes[0]) || "");
    setTestResult((p) => ({ ...p, [vendorKey]: null }));
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditFields({});
    setEditMode("");
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const trimmed = Object.fromEntries(
        Object.entries(editFields).filter(([, v]) => (v || "").trim())
      );
      await saveShippingCredentials(editing, trimmed, editMode);
      toast.success(`${status[editing]?.label || editing} credentials saved`);
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendorKey) => {
    if (!window.confirm(`Remove ${status[vendorKey]?.label || vendorKey} credentials? This will revert to env-var values (if any).`)) return;
    try {
      await deleteShippingCredentials(vendorKey);
      toast.success("Credentials removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Delete failed");
    }
  };

  const handleDeleteField = async (vendorKey, field) => {
    if (!window.confirm(`Remove the saved ${field} value for ${status[vendorKey]?.label || vendorKey}?`)) return;
    try {
      const result = await deleteShippingCredentialField(vendorKey, field);
      if (result?.deleted) toast.success(`${field} removed`);
      else toast.warning(`${field} is not stored in the application`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Field removal failed");
    }
  };

  const handleTest = async (vendorKey) => {
    setTesting(vendorKey);
    setTestResult((p) => ({ ...p, [vendorKey]: null }));
    try {
      const res = await testShippingCredentials(vendorKey);
      setTestResult((p) => ({ ...p, [vendorKey]: res }));
      if (res?.ok) toast.success("Connection OK");
      else toast.error(`Test failed: ${res?.error || "see details"}`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "test failed";
      setTestResult((p) => ({ ...p, [vendorKey]: { ok: false, error: detail } }));
      toast.error(detail);
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500" data-testid="shipping-vendors-loading">Loading shipping vendor configuration…</div>;
  }

  if (!flagEnabled) {
    return (
      <div className="space-y-3" data-testid="shipping-vendors-disabled">
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
          <Warning size={16} className="text-amber-700 mt-0.5" weight="bold" />
          <div className="text-xs leading-snug">
            <div className="font-bold text-amber-900">Direct-vendor shipping is OFF</div>
            <div className="text-amber-800 mt-0.5">
              The legacy <span className="font-mono">usair.online</span> proxy is currently serving shipping. Set
              <span className="font-mono mx-1">SHIPPING_USE_DIRECT_VENDORS=true</span> in <span className="font-mono">backend/.env</span> and restart the backend to enable.
              Once enabled, super-admins can paste vendor credentials here without redeploying.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="shipping-vendors-panel">
      <div>
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Truck size={16} weight="bold" className="text-blue-700" /> Shipping vendor credentials
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Rotate carrier / customs / finance keys at runtime. Values are encrypted with
          <span className="font-mono mx-1">SHIPPING_CRED_KEY</span> before persistence. Env-var
          fallbacks are honoured for fields not saved here. Only super-admins see this tab.
        </p>
      </div>

      <div className="space-y-3">
        {Object.entries(status || {}).map(([vendorKey, vendor]) => {
          const adapter = adapters[vendorKey] || {};
          const isEditing = editing === vendorKey;
          const tr = testResult[vendorKey];
          const stateColor = !vendor.has_any_fields
            ? "slate"
            : (!vendor.configured || !adapter.ready ? "amber" : "blue");
          const stateLabel = !vendor.has_any_fields
            ? "Not configured"
            : !vendor.configured
              ? "Incomplete"
              : !adapter.ready
                ? "Configured - unavailable"
                : vendor.integration_status === "authentication_only"
                  ? "Configured - auth only"
                  : "Configured";
          return (
            <div key={vendorKey}
                 data-testid={`shipping-vendor-row-${vendorKey}`}
                 className="rounded-sm border border-slate-200 bg-white p-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-slate-800">{vendor.label}</span>
                    <span data-testid={`shipping-vendor-state-${vendorKey}`}
                          className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${
                            stateColor === "blue" ? "bg-blue-50 border-blue-200 text-blue-700" :
                            stateColor === "amber" ? "bg-amber-50 border-amber-200 text-amber-700" :
                            "bg-slate-50 border-slate-200 text-slate-500"
                          }`}>
                      {stateLabel}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>Source: <span className="font-mono">{vendor.source || "—"}</span></span>
                    {vendor.mode && <span>Mode: <span className="font-mono">{vendor.mode}</span></span>}
                    {vendor.updated_by && <span>By: <span className="font-mono">{vendor.updated_by}</span></span>}
                    {vendor.updated_at && <span>{new Date(vendor.updated_at).toLocaleString()}</span>}
                  </div>
                  {vendor.missing_fields?.length > 0 && vendor.has_any_fields && (
                    <div className="text-[11px] text-amber-700 mt-1">
                      Missing: <span className="font-mono">{vendor.missing_fields.join("; ")}</span>
                    </div>
                  )}
                  {vendor.status_note && (
                    <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                      {vendor.status_note}
                    </div>
                  )}
                </div>
                {!isEditing && (
                  <div className="flex items-center gap-1.5">
                    <Button
                      data-testid={`shipping-vendor-test-${vendorKey}`}
                      onClick={() => handleTest(vendorKey)}
                      disabled={!vendor.configured || testing === vendorKey}
                      className="h-7 px-2 text-[11px] rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
                    >
                      {testing === vendorKey ? "Testing…" : "Test"}
                    </Button>
                    <Button
                      data-testid={`shipping-vendor-edit-${vendorKey}`}
                      onClick={() => startEdit(vendorKey, vendor)}
                      className="h-7 px-2 text-[11px] rounded-sm bg-blue-700 hover:bg-blue-800 text-white"
                    >
                      Edit
                    </Button>
                    {(vendor.persisted_fields || []).length > 0 && (
                      <Button
                        data-testid={`shipping-vendor-delete-${vendorKey}`}
                        onClick={() => handleDelete(vendorKey)}
                        className="h-7 w-7 p-0 rounded-sm bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
                      >
                        <Trash size={12} />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {tr && (
                <div data-testid={`shipping-vendor-test-result-${vendorKey}`}
                     className={`text-[11px] px-2 py-1.5 rounded-sm border ${
                       tr.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"
                     }`}>
                  {tr.ok ? <CheckCircle size={11} weight="bold" className="inline mr-1" /> : <Warning size={11} weight="bold" className="inline mr-1" />}
                  {tr.ok ? (tr.summary || "Provider check passed") : `Test failed: ${tr.error || JSON.stringify(tr)}`}
                  {tr.ok && tr.verification_scope?.length > 0 && (
                    <span className="ml-1">
                      ({tr.verification_scope.join(", ")})
                    </span>
                  )}
                  {tr.warnings?.map((warning) => (
                    <div key={warning} className="mt-1">{warning}</div>
                  ))}
                </div>
              )}

              {isEditing && (
                <div className="border-t border-slate-100 pt-2 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {vendor.expected_fields.map((field) => {
                      const masked = ["secret", "password", "token", "key"].some(s => field.toLowerCase().includes(s));
                      const visible = showSecret[`${vendorKey}.${field}`];
                      return (
                        <div key={field} className="space-y-1">
                          <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{field}</Label>
                          <div className="flex items-center gap-1">
                            <Input
                              data-testid={`shipping-field-${vendorKey}-${field}`}
                              type={masked && !visible ? "password" : "text"}
                              value={editFields[field] || ""}
                              onChange={(e) => setEditFields((p) => ({ ...p, [field]: e.target.value }))}
                              placeholder={vendor.configured_fields.includes(field) ? "leave blank to keep existing" : `paste ${field}`}
                              className="h-8 text-xs rounded-sm font-mono"
                            />
                            {masked && (
                              <button
                                data-testid={`shipping-field-toggle-${vendorKey}-${field}`}
                                onClick={() => setShowSecret((p) => ({ ...p, [`${vendorKey}.${field}`]: !visible }))}
                                className="h-8 w-8 flex items-center justify-center rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-100"
                                type="button"
                              >
                                {visible ? <EyeSlash size={13} /> : <Eye size={13} />}
                              </button>
                            )}
                            {(vendor.persisted_fields || []).includes(field) && (
                              <button
                                data-testid={`shipping-field-delete-${vendorKey}-${field}`}
                                onClick={() => handleDeleteField(vendorKey, field)}
                                className="h-8 w-8 flex items-center justify-center rounded-sm border border-rose-200 text-rose-700 hover:bg-rose-50"
                                type="button"
                                title={`Remove saved ${field}`}
                              >
                                <Trash size={13} />
                              </button>
                            )}
                          </div>
                          {vendor.field_help?.[field] && (
                            <div className="text-[10px] text-slate-500 leading-snug">
                              {vendor.field_help[field]}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 shrink-0">Mode</Label>
                    {vendor.modes.length > 1 ? (
                      <select
                        data-testid={`shipping-mode-${vendorKey}`}
                        value={editMode}
                        onChange={(e) => setEditMode(e.target.value)}
                        className="h-8 text-xs rounded-sm border border-slate-300 px-2 bg-white"
                      >
                        {vendor.modes.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    ) : (
                      <span data-testid={`shipping-mode-${vendorKey}`}
                            className="h-8 inline-flex items-center px-2 text-xs font-mono border border-slate-200 bg-slate-50 rounded-sm">
                        {vendor.modes[0]}
                      </span>
                    )}
                    <div className="flex-1" />
                    <Button
                      data-testid={`shipping-vendor-cancel-${vendorKey}`}
                      onClick={cancelEdit}
                      className="h-8 px-2.5 text-xs rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid={`shipping-vendor-save-${vendorKey}`}
                      onClick={handleSave}
                      disabled={saving}
                      className="h-8 px-3 text-xs rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
                    >
                      {saving ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

